/* ═══════════════════════════════════════════════════════════════════
   [D-321] Посилення — керування з дашборду.

   🔴 ПЕРЕРОБКА ЧИТАБЕЛЬНОСТІ (скрін OWNER 2026-09-12):
   на темній темі підписи виходили сірими на майже чорному, а крізь картки
   просвічував фоновий логотип — текст практично не читався. Тому тут НІДЕ
   не покладаємось на `text.secondary` і напівпрозорі фони: усі кольори
   задані явно, картки — щільні.

   [D-324] ВКЛАДКИ замість одного довгого списку (вимога OWNER 2026-09-16):
   «щоб не створювалось незручне меню у вигляді одного огромного списка».
   Розділів тут ставатиме більше, тож кожен новий іде у свою вкладку, а не
   в кінець стрічки. Видача токенів юзеру переїхала у картку користувача —
   їй місце там, де решта дій над конкретною людиною.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, ToggleButton, ToggleButtonGroup, TextField,
  Switch, FormControlLabel, Snackbar, Alert, Slider, Chip, Tabs, Tab, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import api from '../api';
// [D-426] порівняння ДО/ПІСЛЯ — окремим файлом, щоб не чіпати логіку сторінки
import HyperCompare from '../components/HyperCompare';

const C = {
  card: '#16202c',        // щільний, щоб фон не просвічував
  line: 'rgba(0,229,160,.22)',
  txt: '#eaf6ff',
  dim: '#a8bece',         // світліший за дефолтний secondary — його не було видно
  neon: '#00e5a0',
  neonTxt: '#7ffcd3',
  coral: '#ff7a59',
};

// [D-474] Набір функцій теми (G45): що тема вмикає / вимикає / не чіпає. Ефекти fx_* тема може лише вимкнути —
// відкрити заборонене партнеру не може (обробка звіряє з його «🧩 Функції»).
const TH_FX = [
  ['director', '🎥 Рух камери'], ['mirror', '🪞 Дзеркало'], ['subs', '💬 Субтитри'], ['fx_inset', '⭕ Врізка'],
  ['fx_breaks', '📰 Перебивки'], ['fx_accent', '🎨 Виділення слів'], ['bg', '🌫 Фон'], ['fx_layouts', '🧍 Розкладки автора'],
  ['bg_grad', '🌈 Кольоровий фон'], ['cards', '🃏 Картки-ілюстрації'], ['fx_broll', '🖼 Ілюстрації під слова'],
  ['fx_info', '📊 Інфографіка'], ['arrows', '➡️ Стрілки'], ['gentle', '🕊 Делікатний режим'],
  ['fx_appear', '✨ Поява (обкладинка)'],   // [D-511] кадр-обкладинка на початку, тане поверх відео; у Студії — увімкнена наперед
];
const TH_COLOR = [['', 'не чіпати'], ['sharp', 'Різкий'], ['soft', 'Мʼякий'], ['business', 'Діловий'], ['neon', 'Неон']];
const TH_SUB = [['', 'не чіпати'], ['classic', 'Класичні'], ['pop', 'Ефектні'], ['hero', 'Великі'], ['both', 'Класичні+ефектні']];

const MODES = [
  { v: 'off', t: 'Вимкнено' },
  { v: 'admin', t: 'Тільки я' },
  { v: 'all', t: 'Усім' },
];

// 🔴 Ключі мають бути РІЗНІ. Тут два розділи ділили ключ 'fx', і
// `tab === 'fx'` було істинним для обох блоків JSX — вони малювались
// разом, а перемикання між ними нічого не давало.
const TABS = [
  { k: 'fx', t: '🎬 Ефекти й ціна' },
  { k: 'dir', t: '🎥 Монтаж' },
  { k: 'micro', t: '🔬 Мікро-ефекти' },
  { k: 'dub', t: '🌍 Переклад озвучки' },
  { k: 'pay', t: '💳 Оплата' },
];

// [D-357] Готові набори. «Як зараз» лишено навмисно — щоб було з чим
// порівняти в прев'ю й куди повернутись, якщо спокійний варіант не зайде.
const PRESETS = [
  { k: 'calm', t: 'Делікатно', d: 'ледь помітно',
    v: { uniq_dir_pace: '7', uniq_dir_strength: '50',
         uniq_dir_scene_events: '2', uniq_dir_scene_zone: '0',
         uniq_dir_scene_start_min: '-3', uniq_dir_scene_start_max: '3',
         uniq_dir_scene_end_min: '-3', uniq_dir_scene_end_max: '3',
         uniq_dir_intro_amp: '2', uniq_dir_intro_dur: '2' } },
  { k: 'mid', t: 'Помірно', d: 'видно, але не заважає',
    v: { uniq_dir_pace: '5', uniq_dir_strength: '90',
         uniq_dir_scene_events: '3', uniq_dir_scene_zone: '0',
         uniq_dir_scene_start_min: '-5', uniq_dir_scene_start_max: '5',
         uniq_dir_scene_end_min: '-5', uniq_dir_scene_end_max: '5',
         uniq_dir_intro_amp: '3', uniq_dir_intro_dur: '1.5' } },
  { k: 'now', t: 'Як зараз', d: 'ритм 1.5 с, сила 200 %',
    v: { uniq_dir_pace: '1.5', uniq_dir_strength: '200',
         uniq_dir_scene_events: '0', uniq_dir_scene_zone: '0',
         uniq_dir_scene_start_min: '-5', uniq_dir_scene_start_max: '5',
         uniq_dir_scene_end_min: '-5', uniq_dir_scene_end_max: '5',
         uniq_dir_intro_amp: '3', uniq_dir_intro_dur: '1.5' } },
];

// [D-360] 🔴 `Card` МАЄ жити тут, а не в тілі `HyperSettingsPage`.
// Усередині компонента це щоразу нова функція, React бачить інший тип
// і перемонтовує піддерево на кожному рендері. Канвас прев'ю через це
// ставав порожнім DOM-вузлом кожні 125 мс, а контекст малювання, взятий
// у замиканні, писав у старий, уже від'єднаний елемент — на екрані
// лишався чистий CSS-фон.
// [D-363] Пояснення ховається за власною кнопкою: відкрив картку,
// захотів подробиць — натиснув ще раз. Інакше описи розтягують список
// так, що до ручок доводиться гортати.
const Note = ({ children, label }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button size="small" onClick={() => setOpen(o => !o)}
        sx={{ textTransform: 'none', fontSize: 11.5, color: C.dim,
              px: .8, minWidth: 0, mt: .5,
              '&:hover': { color: C.neonTxt, bgcolor: 'transparent' } }}>
        {open ? '▾ сховати пояснення' : `▸ ${label || 'як це працює'}`}
      </Button>
      {open && (
        <Typography sx={{ fontSize: 12, color: C.dim, mt: .8,
                          lineHeight: 1.6, pl: .8,
                          borderLeft: `2px solid ${C.line}` }}>
          {children}
        </Typography>
      )}
    </>
  );
};

// [D-363] Картка згортається кліком по заголовку й переставляється
// стрілками. 🔴 `Card` МАЄ лишатись на рівні модуля — усередині
// компонента це нова функція на кожному рендері, React перемонтовує
// піддерево, і канвас прев'ю знову стає німим (D-360).
const Card = ({ title, chip, chipColor, children, ui }) => {
  const open = ui ? ui.isOpen(title) : true;
  return (
    <Paper elevation={0} sx={{
      p: 2.5, mb: 2, borderRadius: 3, bgcolor: C.card,
      border: `1px solid ${C.line}`, backgroundImage: 'none',
      breakInside: 'avoid', WebkitColumnBreakInside: 'avoid',
      ...(ui ? { order: ui.orderOf(title) } : {}),
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
                 mb: open ? 1.8 : 0 }}>
        <Box onClick={() => ui && ui.toggle(title)}
             sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1,
                   cursor: ui ? 'pointer' : 'default', minWidth: 0,
                   userSelect: 'none' }}>
          {ui && (
            <Typography sx={{ color: C.neon, fontSize: 13, width: 14,
                              flexShrink: 0 }}>
              {open ? '▾' : '▸'}
            </Typography>
          )}
          <Typography sx={{ fontWeight: 800, fontSize: 15.5, color: C.txt }}>
            {title}
          </Typography>
          {chip && (
            <Chip size="small" label={chip} sx={{
              height: 22, fontSize: 11.5, fontWeight: 700,
              bgcolor: chipColor === 'coral' ? 'rgba(255,122,89,.18)' : 'rgba(0,229,160,.16)',
              color: chipColor === 'coral' ? C.coral : C.neonTxt,
            }} />
          )}
        </Box>
        {ui && ui.move && (
          <Box sx={{ display: 'flex', gap: .2, flexShrink: 0 }}>
            <Button size="small" onClick={(e) => { e.stopPropagation(); ui.move(title, -1); }}
              sx={{ minWidth: 26, px: 0, color: C.dim, fontSize: 13,
                    '&:hover': { color: C.neon } }}>↑</Button>
            <Button size="small" onClick={(e) => { e.stopPropagation(); ui.move(title, 1); }}
              sx={{ minWidth: 26, px: 0, color: C.dim, fontSize: 13,
                    '&:hover': { color: C.neon } }}>↓</Button>
          </Box>
        )}
      </Box>
      {open && children}
    </Paper>
  );
};

export default function HyperSettingsPage() {
  const [s, setS] = useState(null);
  const [toast, setToast] = useState('');
  const [err, setErr] = useState('');
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem('hyper_tab_v1') || 'fx'; } catch (e) { return 'fx'; }
  });
  // [D-324] платіжні тумблери: ТІ САМІ ключі, що в ⚙️ Налаштуваннях.
  // Не дублюємо стан у базі — лише показуємо тут, бо саме звідси OWNER керує
  // продажем токенів.
  const [pay, setPay] = useState(null);
  const [monoOn, setMonoOn] = useState(false);

  // ── [D-358] прев'ю: стан ─────────────────────────────────────────
  const pvCanvas = useRef(null);
  const pvGraph = useRef(null);
  const pvMini = useRef(null);   // [D-369] схема «скільки кадру видно»
  const pvRaf = useRef(0);
  const pvTimeRef = useRef(0);
  const [pvData, setPvData] = useState(null);
  const [pvPlay, setPvPlay] = useState(true);
  const [pvSeed, setPvSeed] = useState(0);
  const [pvDur, setPvDur] = useState(30);
  const [pvTime, setPvTime] = useState(0);
  const [themeName, setThemeName] = useState('');
  const [thFx, setThFx] = useState(null);   // [D-474] { slot, n, fx, color, sub } — редагування набору теми
  // [D-377] велика діаграма з перетягуванням опорних точок
  const [profOpen, setProfOpen] = useState(false);
  // [D-378] «збережено» ще не означає «видно»: крива рахується на
  // сервері, і кілька секунд незрозуміло, чи зміна взагалі дійшла.
  const [pvBusy, setPvBusy] = useState(false);
  const [pvFlash, setPvFlash] = useState(false);
  const [dlgPos, setDlgPos] = useState({ x: 0, y: 0 });
  const dlgDrag = useRef(null);
  const profCanvas = useRef(null);
  const profDrag = useRef(-1);
  // лічильник, яким будимо ефект, коли канвас нарешті з'явився
  const [profTick, setProfTick] = useState(0);
  // [D-426] що щойно змінили — прев'ю ДО/ПІСЛЯ перескакує на цей момент
  const [hcFocus, setHcFocus] = useState({ keys: [], n: 0 });
  // [D-382] Голова й хвіст — по 3 секунди з кроком 0.5; середина
  // розтягується під тривалість. Саме краї платформа порівнює
  // найпильніше, і саме там рівномірна сітка давала крок у кілька
  // секунд.
  // [D-424] перші 4 с — 9 точок по 0.5 с (було 3 с / 7), середина — 8 точок;
  // нижче нуля — віддалення (краї заповнює розмита копія кадру)
  const PROF_HEAD = 9, PROF_MID = 8, PROF_TAIL = 7;
  const PROF_N = PROF_HEAD + PROF_MID + PROF_TAIL;
  const PROF_MAX = 40;          // стеля, % зуму
  const PROF_MIN = -30;         // [D-424] найглибше віддалення в редакторі, %
  const profVals = React.useMemo(() => {
    const raw = String((s && s.uniq_dir_profile) || '').trim();
    if (!raw) return new Array(PROF_N).fill(0);
    if (raw.indexOf('=') >= 0) {
      // зонний формат H=…|M=…|T=…
      const z = { H: [], M: [], T: [] };
      raw.split('|').forEach(p => {
        const i = p.indexOf('=');
        if (i < 0) return;
        const k = p.slice(0, i).trim().toUpperCase();
        if (!z[k]) return;
        z[k] = p.slice(i + 1).split(',').map(x => parseFloat(x))
          .filter(x => !isNaN(x));
      });
      const fit = (arr, n) => {
        if (!arr.length) return new Array(n).fill(0);
        const out = [];
        for (let i = 0; i < n; i++) {
          const t = n === 1 ? 0 : i / (n - 1) * (arr.length - 1);
          const a = Math.floor(t), b = Math.min(arr.length - 1, a + 1);
          out.push(arr[a] + (arr[b] - arr[a]) * (t - a));
        }
        return out;
      };
      // [D-424] стара голова (7 точок = 3 с) переноситься ЗА ЧАСОМ: ті самі
      // значення на 0…3 с, далі — перше значення середини. Форма не міняється.
      const head = z.H.length === 7
        ? [...z.H, ...new Array(PROF_HEAD - 7).fill(z.M.length ? z.M[0] : z.H[6])]
        : fit(z.H, PROF_HEAD);
      return [...head, ...fit(z.M, PROF_MID), ...fit(z.T, PROF_TAIL)];
    }
    // старий рівномірний рядок — розтягуємо на нову сітку
    const v = raw.split(',').map(x => parseFloat(x)).filter(x => !isNaN(x));
    if (v.length < 2) return new Array(PROF_N).fill(0);
    const out = [];
    for (let i = 0; i < PROF_N; i++) {
      const t = i / (PROF_N - 1) * (v.length - 1);
      const a = Math.floor(t), b = Math.min(v.length - 1, a + 1);
      out.push(v[a] + (v[b] - v[a]) * (t - a));
    }
    return out;
  }, [s]);
  const saveProf = (vals) => {
    const f = (a) => a.map(v => Number(v).toFixed(1)).join(',');
    save({ uniq_dir_profile:
      `H=${f(vals.slice(0, PROF_HEAD))}` +
      `|M=${f(vals.slice(PROF_HEAD, PROF_HEAD + PROF_MID))}` +
      `|T=${f(vals.slice(PROF_HEAD + PROF_MID))}` });
  };
  // [D-370] який розділ відкритий; порожньо — жоден
  const [section, setSection] = useState(() => {
    try { return localStorage.getItem('hyper_section_v1') || 's2'; }
    catch (e) { return 's2'; }
  });
  useEffect(() => {
    try { localStorage.setItem('hyper_section_v1', section); } catch (e) {}
  }, [section]);

  // [D-363] Згорнуті картки й їхній порядок. Тримаємо в localStorage:
  // це суто вигляд, у базі йому робити нема чого, а між заходами
  // зберегтись має.
  const [uiShut, setUiShut] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hyper_shut_v1') || '[]'); }
    catch (e) { return []; }
  });
  const [uiOrder, setUiOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hyper_order_v1') || '[]'); }
    catch (e) { return []; }
  });
  const putShut = (v) => {
    setUiShut(v);
    try { localStorage.setItem('hyper_shut_v1', JSON.stringify(v)); } catch (e) {}
  };
  const putOrder = (v) => {
    setUiOrder(v);
    try { localStorage.setItem('hyper_order_v1', JSON.stringify(v)); } catch (e) {}
  };
  const DIR_CARDS = [
    'Ілюстрації: дефолт', 'Фон-градієнт: дефолт', 'Режисер: дефолт',
    'Вступний рух: перші секунди', 'Зум усередині сцени',
    'Скільки ефектів і де саме', 'Якість маски автора', 'Як це працює',
  ];
  const cardUI = {
    isOpen: (t) => !uiShut.includes(t),
    toggle: (t) => putShut(uiShut.includes(t)
      ? uiShut.filter(x => x !== t) : [...uiShut, t]),
    // Картка, якої ще немає в порядку, йде в кінець списку відомих, але
    // перед тими, що з'являться пізніше — інакше новий розділ
    // вистрибував би на початок.
    orderOf: (t) => {
      const i = uiOrder.indexOf(t);
      return i < 0 ? 50 : i;
    },
    move: (t, d) => {
      // 🔴 Перший рух має відштовхуватись від ПОВНОГО списку. Інакше
      // картка лишалась би єдиною в порядку, отримувала order 0 і
      // стрибала на початок замість сусідньої позиції.
      const base = uiOrder.length ? [...uiOrder] : [...DIR_CARDS];
      const cur = base.includes(t) ? base : [...base, t];
      const i = cur.indexOf(t);
      const j = i + d;
      if (j < 0 || j >= cur.length) return;
      [cur[i], cur[j]] = [cur[j], cur[i]];
      putOrder(cur);
    },
    reset: () => { putOrder([]); putShut([]); },
  };
  // те саме згортання, але без стрілок — див. коментар вище
  // [D-365] Назви карток «Монтажу» — для «згорнути все». Беремо
  // списком, а не з DOM: заголовок і так є ключем стану згортання.
  const cardFold = {
    isOpen: cardUI.isOpen,
    toggle: cardUI.toggle,
    orderOf: () => 0,
  };


  // Ключі, з яких складається «тема». У сховищі лежать БЕЗ префікса
  // `uniq_dir_`: на значення налаштування є ліміт 500 символів, а з
  // повними іменами шістнадцять параметрів у нього не влазять.
  const THEME_KEYS = [
    'pace', 'strength', 'intro_on', 'intro_amp', 'intro_dur', 'intro_solo',
    'scene_on', 'scene_moves_min', 'scene_moves_max', 'scene_start_min',
    'scene_start_max', 'scene_end_min', 'scene_end_max', 'scene_speed_max',
    'scene_events', 'scene_zone', 'manual',
  ];
  const THEME_SLOTS = 12;

  const themes = React.useMemo(() => {
    if (!s) return [];
    const out = [];
    for (let i = 1; i <= THEME_SLOTS; i++) {
      const raw = s[`uniq_theme_${i}`];
      if (!raw) continue;
      try {
        const o = JSON.parse(raw);
        if (!o || !o.n) continue;
        const v = {};
        THEME_KEYS.forEach(k => {
          if (o.v && o.v[k] !== undefined) v[`uniq_dir_${k}`] = String(o.v[k]);
        });
        // [D-383] профіль живе окремим ключем: разом із рештою він не
        // влазив у ліміт 500 символів на значення
        v.uniq_dir_profile = String(s[`uniq_themep_${i}`] || '');
        out.push({ slot: i, n: o.n, v });
      } catch (e) { /* зіпсований слот просто пропускаємо */ }
    }
    return out;
  }, [s]);

  const saveTheme = () => {
    const nm = themeName.trim();
    if (!nm || !s) return;
    // [D-533] спершу шукаємо тему з такою назвою (оновлення), і лише потім — вільне місце. Раніше вільний слот 1 чи 2 траплявся
    // першим, і «та сама назва» створювала ДУБЛЬ замість оновлення.
    const same = themes.find(t => String(t.n).trim().toLowerCase() === nm.toLowerCase());
    let slot = same ? same.slot : 0;
    for (let i = 1; !slot && i <= THEME_SLOTS; i++) {
      if (!themes.find(t => t.slot === i)) slot = i;
    }
    if (!slot) { setErr('Більше 12 тем не вміщається — видаліть зайву (×)'); return; }
    if (same && !window.confirm('Тема «' + same.n + '» уже є. Оновити її поточними налаштуваннями? Вона спільна — зміна діє в усіх, хто її вибирає.')) return;
    let keepFx = null;   // старі теми тримають «що вмикає» всередині запису — не губимо при оновленні
    try { keepFx = (JSON.parse(s[`uniq_theme_${slot}`] || '{}') || {}).fx || null; } catch (e) { keepFx = null; }
    const v = {};
    THEME_KEYS.forEach(k => {
      const val = s[`uniq_dir_${k}`];
      if (val !== undefined && val !== null && val !== '') v[k] = String(val);
    });
    save({
      [`uniq_theme_${slot}`]: JSON.stringify(same && keepFx ? { n: same.n, v, fx: keepFx } : { n: same ? same.n : nm, v }),
      [`uniq_themep_${slot}`]: String(s.uniq_dir_profile || ''),
    });
    setThemeName('');
  };

  const delTheme = (slot) => save({
    [`uniq_theme_${slot}`]: '', [`uniq_themep_${slot}`]: '', [`uniq_themefx_${slot}`]: '' });
  // [D-474] набір теми: старі теми мали fx усередині uniq_theme_N (D-404) — підхоплюємо, далі пишемо окремо
  const openThFx = (th) => {
    let inl = {}, ext = {};
    try { inl = (JSON.parse(s[`uniq_theme_${th.slot}`] || '{}') || {}).fx || {}; } catch (e) { inl = {}; }
    try { ext = JSON.parse(s[`uniq_themefx_${th.slot}`] || '{}') || {}; } catch (e) { ext = {}; }
    setThFx({ slot: th.slot, n: th.n, fx: { ...inl, ...(ext.fx || {}) }, color: ext.color || '', sub: ext.sub || '' });
  };
  const thFxCount = (slot) => {
    let inl = {}, ext = {};
    try { inl = (JSON.parse((s && s[`uniq_theme_${slot}`]) || '{}') || {}).fx || {}; } catch (e) { inl = {}; }
    try { ext = JSON.parse((s && s[`uniq_themefx_${slot}`]) || '{}') || {}; } catch (e) { ext = {}; }
    return Object.keys({ ...inl, ...(ext.fx || {}) }).length + (ext.color ? 1 : 0) + (ext.sub ? 1 : 0);
  };
  const saveThFx = () => {
    const fx = {};
    Object.entries(thFx.fx || {}).forEach(([k, v]) => { if (v === 0 || v === 1) fx[k] = v; });
    save({ [`uniq_themefx_${thFx.slot}`]: JSON.stringify({ fx, color: thFx.color || undefined, sub: thFx.sub || undefined }) });
    setThFx(null);
  };

  // Який пресет зараз активний — порівнюємо значення, а не пам'ятаємо
  // останній клік: інакше після ручної правки кнопка лишалась би
  // підсвіченою й брехала.
  const activePreset = React.useMemo(() => {
    if (!s) return null;
    const hit = PRESETS.find(pr =>
      Object.keys(pr.v).every(k => String(s[k] ?? '') === String(pr.v[k])));
    return hit ? hit.k : null;
  }, [s]);

  // Різниця з оригіналом. Оригінал — це зум рівно 1.0; усе, що вище,
  // означає обрізаний кадр. OWNER питав саме про першу долю секунди:
  // якщо там 1.00, ролик стартує як оригінал, і для соцмережі це
  // найгірше місце.
  const pvDiff = React.useMemo(() => {
    const pts = (pvData && pvData.points) || [];
    if (!pts.length) return { now: '—', first: '—', max: '—', seen: '—', firstBad: false };
    const pct = (z) => `${((z - 1) * 100).toFixed(1)} %`;
    let cur = pts[0];
    for (let i = 1; i < pts.length; i++) {
      if (pts[i].t >= pvTime) { cur = pts[i - 1]; break; }
      cur = pts[i];
    }
    const zmax = Math.max(...pts.map(p => p.z));
    return {
      // яка частка ширини оригіналу лишається в кадрі
      seen: cur.z < 1 ? 'увесь + поля' : `${(100 / Math.max(1, cur.z)).toFixed(0)} %`,   // [D-426]
      now: pct(cur.z),
      first: pct(pts[0].z),
      max: pct(zmax),
      firstBad: Math.abs(pts[0].z - 1) * 100 < 1.0,   // [D-426] віддалення теж різниця
    };
  }, [pvData, pvTime]);

  const setTabSafe = (v) => {
    setTab(v);
    try { localStorage.setItem('hyper_tab_v1', v); } catch (e) {}
  };

  useEffect(() => {
    api.get('/referral/settings')
      .then(r => setS(r.data || {}))
      .catch(() => setErr('Не вдалося завантажити'));
  }, []);

  // [D-407] Довідник кнопок «Посилення»: назви, описи, порядок, дефолти.
  // Значення й видимість тут НЕ зберігаються — вони лежать у
  // referral_settings під `uniq_hyper_<key>` і `uniq_hyper_vis_<key>`,
  // тобто там само, де й були три старі ключі. Одне джерело на значення.
  const [feats, setFeats] = useState([]);
  useEffect(() => {
    api.get('/uniq/feature-registry')
      .then(r => setFeats(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFeats([]));   // не завантажився — нижче старий список
  }, []);

  const loadPay = () => {
    api.get('/media/payment-config').then(r => setPay(r.data || {})).catch(() => setPay({}));
    api.get('/media/mono/config').then(r => setMonoOn(!!(r.data || {}).enabled)).catch(() => {});
  };
  useEffect(() => { if (tab === 'pay' && pay === null) loadPay(); }, [tab, pay]);


  // [D-358] крива руху — з API, бо її рахує той самий код, що й відео
  useEffect(() => {
    if (!['dir', 'fx', 'micro'].includes(tab) || !s) return undefined;   // [D-426]
    const body = {
      duration: pvDur, fps: 30, seed: pvSeed, versions: 4,
      pace: s.uniq_dir_pace, strength: s.uniq_dir_strength,
      intro_on: s.uniq_dir_intro_on, intro_amp: s.uniq_dir_intro_amp,
      intro_dur: s.uniq_dir_intro_dur,
      scene_on: s.uniq_dir_scene_on,
      scene_moves_min: s.uniq_dir_scene_moves_min,
      scene_moves_max: s.uniq_dir_scene_moves_max,
      scene_start_min: s.uniq_dir_scene_start_min,
      scene_start_max: s.uniq_dir_scene_start_max,
      scene_end_min: s.uniq_dir_scene_end_min,
      scene_end_max: s.uniq_dir_scene_end_max,
      scene_speed_max: s.uniq_dir_scene_speed_max,
      scene_events: s.uniq_dir_scene_events,
      scene_zone: s.uniq_dir_scene_zone,
      // [D-368] Без цих трьох вимкнення монтажу й ввімкнення
      // ілюстрацій не міняли в прев'ю нічого — саме на це скаржився
      // OWNER.
      dir_enabled: s.uniq_dir_enabled,
      profile: s.uniq_dir_profile || '',
      manual: s.uniq_dir_manual || '0',
      bg_gradient: s.uniq_bg_gradient,
      broll_cards: s.uniq_broll_cards,
    };
    let dead = false;
    setPvBusy(true);
    api.post('/uniq/preview_curve', body)
      .then(r => {
        if (dead) return;
        setPvData(r.data || null);
        setPvBusy(false);
        // [D-378] спалах рівно 5 с — щоб було видно, куди дивитись
        setPvFlash(true);
        setTimeout(() => setPvFlash(false), 5000);
      })
      .catch(() => { if (!dead) { setPvData(null); setPvBusy(false); } });
    return () => { dead = true; };
  }, [tab, s, pvSeed, pvDur]);

  // Малювання. 🔴 Час живе у ref, а не в стані: якби кадр анімації
  // щоразу викликав setPvTime, ефект перезапускався б шістдесят разів
  // на секунду й сам себе вбивав. У стан він потрапляє рідко — лише
  // щоб рухався повзунок і оновлювались підписи.
  useEffect(() => {
    if (tab !== 'dir') return undefined;
    const cv = pvCanvas.current, gr = pvGraph.current;
    if (!cv || !pvData || !pvData.points || !pvData.points.length) return undefined;
    const pts = pvData.points;
    const dur = pts[pts.length - 1].t || 1;

    const at = (el) => {
      for (let i = 1; i < pts.length; i++) {
        if (pts[i].t >= el) {
          const a = pts[i - 1], b = pts[i];
          const k = (el - a.t) / Math.max(1e-6, b.t - a.t);
          return { z: a.z + (b.z - a.z) * k, x: a.x + (b.x - a.x) * k };
        }
      }
      return pts[pts.length - 1];
    };

    const drawGraph = (el) => {
      const grNow = pvGraph.current;
      if (!grNow) return;
      const g = grNow.getContext('2d');
      if (!g) return;
      const W = grNow.width, H = grNow.height;
      g.clearRect(0, 0, W, H);
      const zs = pts.map(p => p.z);
      const lo = Math.min(1, ...zs), hi = Math.max(...zs);
      const span = Math.max(0.001, hi - lo);
      const yOf = (z) => H - 6 - (H - 12) * (z - lo) / span;
      const marks = pvData.marks || {};
      // [D-368] Спершу смуги, потім крива поверх них: інакше заливка
      // перекрила б лінію. Крива без розмітки показувала, ЩО
      // смикається, але не ЧОМУ.
      (marks.drift || []).forEach(([a, b]) => {
        g.fillStyle = 'rgba(0,229,160,.13)';
        g.fillRect(W * a / dur, 0, W * (b - a) / dur, H);
      });
      if (marks.intro && marks.intro.on) {
        g.fillStyle = 'rgba(77,166,255,.20)';
        g.fillRect(0, 0, W * Math.min(marks.intro.dur, dur) / dur, H);
      }
      g.strokeStyle = 'rgba(234,246,255,.20)'; g.lineWidth = 1;
      (marks.cuts || []).forEach((t) => {
        const x = W * t / dur;
        g.beginPath(); g.moveTo(x, 0); g.lineTo(x, H); g.stroke();
      });
      // синя пунктирна — оригінал (зум 1.0)
      g.strokeStyle = '#4da6ff'; g.lineWidth = 1.5;
      g.setLineDash([4, 3]);
      g.beginPath(); g.moveTo(0, yOf(1)); g.lineTo(W, yOf(1)); g.stroke();
      g.setLineDash([]);
      g.strokeStyle = '#00e5a0'; g.lineWidth = 2; g.beginPath();
      pts.forEach((p, i) => {
        const x = W * p.t / dur;
        if (i === 0) g.moveTo(x, yOf(p.z)); else g.lineTo(x, yOf(p.z));
      });
      g.stroke();
      g.strokeStyle = 'rgba(234,246,255,.5)'; g.lineWidth = 1;
      const cx = W * Math.min(el, dur) / dur;
      g.beginPath(); g.moveTo(cx, 0); g.lineTo(cx, H); g.stroke();
    };

    // [D-369] Сцена малюється в координатах ОРИГІНАЛУ, а показ
    // масштабується тією самою трансформацією, що й у ffmpeg. Тому
    // автор справді росте й їде по екрану, а не лишається на місці під
    // рухомою рамкою.
    const drawScene = (ctx, W, H, sc) => {
      if (sc.bg_gradient) {
        const gd = ctx.createLinearGradient(0, 0, W, H);
        gd.addColorStop(0, '#1b2a6b'); gd.addColorStop(1, '#0a1230');
        ctx.fillStyle = gd;
      } else {
        ctx.fillStyle = '#16202c';
      }
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = 'rgba(168,190,206,.16)'; ctx.lineWidth = 1;
      for (let i = 1; i < 6; i++) {
        ctx.beginPath(); ctx.moveTo(W * i / 6, 0); ctx.lineTo(W * i / 6, H); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, H * i / 6); ctx.lineTo(W, H * i / 6); ctx.stroke();
      }
      // автор: голова на 0.45 висоти — там, де його шукає режисер
      ctx.fillStyle = 'rgba(0,229,160,.55)';
      ctx.beginPath();
      ctx.arc(W * 0.5, H * 0.34, Math.min(W, H) * 0.085, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(W * 0.5, H * 0.43);
      ctx.lineTo(W * 0.5 - W * 0.13, H * 0.78);
      ctx.lineTo(W * 0.5 + W * 0.13, H * 0.78);
      ctx.closePath();
      ctx.fill();
      if (sc.broll_cards) {
        for (let i = 0; i < 3; i++) {
          ctx.save();
          ctx.translate(W * 0.5, H * 0.88);
          ctx.rotate((i - 1) * 0.12);
          ctx.fillStyle = 'rgba(255,255,255,.85)';
          ctx.fillRect(-W * 0.10, -H * 0.05, W * 0.20, H * 0.10);
          ctx.strokeStyle = 'rgba(0,0,0,.35)'; ctx.lineWidth = 1;
          ctx.strokeRect(-W * 0.10, -H * 0.05, W * 0.20, H * 0.10);
          ctx.restore();
        }
      }
    };

    const render = (el) => {
      const cvNow = pvCanvas.current;
      if (!cvNow) return;
      const ctx2 = cvNow.getContext('2d');
      if (!ctx2) return;
      const p = at(el);
      const W = cvNow.width, H = cvNow.height;
      const sc = pvData.scene || {};
      const z = Math.max(1, p.z);
      const vx = (W - W / z) * p.x;
      const vy = (H - H / z) * 0.45;

      ctx2.fillStyle = '#05080c'; ctx2.fillRect(0, 0, W, H);
      if (p.z < 0.999) {
        // [D-426] віддалення (D-424): кадр менший, довкола — розмита копія, як у ролику
        ctx2.save(); ctx2.filter = 'blur(6px) brightness(.6)'; drawScene(ctx2, W, H, sc); ctx2.restore();
        ctx2.save();
        ctx2.translate(W * (1 - p.z) / 2, H * (1 - p.z) / 2); ctx2.scale(p.z, p.z);
        drawScene(ctx2, W, H, sc);
        ctx2.restore();
      } else {
      ctx2.save();
      ctx2.beginPath(); ctx2.rect(0, 0, W, H); ctx2.clip();
      ctx2.scale(z, z);
      ctx2.translate(-vx, -vy);
      drawScene(ctx2, W, H, sc);
      ctx2.restore();
      }

      // підписи поверх
      ctx2.fillStyle = 'rgba(5,8,12,.75)';
      ctx2.fillRect(0, H - 26, W, 26);
      ctx2.fillStyle = '#7ffcd3';
      ctx2.font = 'bold 13px monospace';
      ctx2.fillText(`${el.toFixed(2)}s`, 8, H - 8);
      ctx2.fillStyle = Math.abs(p.z - 1) * 100 < 1 ? '#ff7a59' : (p.z < 1 ? '#ff5ca8' : '#7ffcd3');
      ctx2.fillText(`${p.z >= 1 ? '+' : ''}${((p.z - 1) * 100).toFixed(1)}%`, W - 70, H - 8);   // [D-426]

      // маленька схема: скільки кадру зараз видно
      const mini = pvMini.current;
      if (mini) {
        const m = mini.getContext('2d');
        if (m) {
          const MW = mini.width, MH = mini.height;
          m.fillStyle = '#0b0f14'; m.fillRect(0, 0, MW, MH);
          // 🔴 Синю рамку малюємо ТОВСТОЮ і з полем усередину, а зелене
          // вікно стискаємо на ту саму товщину. Інакше при зумі ~1.0
          // зелене накриває синє повністю, і підказка про «синім —
          // оригінал» показує на колір, якого не видно.
          const PAD = 4;
          m.fillStyle = 'rgba(77,166,255,.16)';
          m.fillRect(0, 0, MW, MH);
          m.strokeStyle = '#4da6ff'; m.lineWidth = 3;
          m.strokeRect(1.5, 1.5, MW - 3, MH - 3);
          const iw = MW - PAD * 2, ih = MH - PAD * 2;
          const rw = iw / z, rh = ih / z;
          const rx = PAD + (iw - rw) * p.x, ry = PAD + (ih - rh) * 0.45;
          m.fillStyle = 'rgba(0,229,160,.30)';
          m.fillRect(rx, ry, rw, rh);
          m.strokeStyle = '#00e5a0'; m.lineWidth = 2;
          m.strokeRect(rx, ry, rw, rh);
        }
      }
      drawGraph(el);
    };

    if (!pvPlay) {
      pvTimeRef.current = Math.min(pvTime, dur);
      render(pvTimeRef.current);
      return undefined;
    }
    let t0 = 0, lastSync = 0;
    const loop = (ts) => {
      if (!t0) t0 = ts - pvTimeRef.current * 1000;
      const el = ((ts - t0) / 1000) % dur;
      pvTimeRef.current = el;
      render(el);
      // у стан — разів вісім на секунду, щоб повзунок їхав, а ефект
      // не перезапускався щокадру
      if (ts - lastSync > 120) { lastSync = ts; setPvTime(el); }
      pvRaf.current = requestAnimationFrame(loop);
    };
    pvRaf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(pvRaf.current);
    // pvTime у залежностях — щоб перемальовувалось при перетягуванні
    // повзунка. Під час гри це дає перезапуск разів вісім на секунду;
    // анімація від того не страждає, бо час відновлюється з ref.
  }, [tab, pvData, pvPlay, pvDur, pvTime]);

  // [D-377] велика діаграма: профіль точками, реальна крива позаду
  useEffect(() => {
    if (!profOpen) return undefined;
    // 🔴 [D-378] Dialog монтує вміст ПІСЛЯ того, як стан став true —
    // на першому проході канваса ще немає. Раніше ми просто виходили,
    // і діаграма лишалась порожньою, поки щось не змінювалось.
    let cv = profCanvas.current;
    if (!cv) {
      let tries = 0;
      let raf = 0;
      const wait = () => {
        if (profCanvas.current || tries > 60) { setProfTick(t => t + 1); return; }
        tries += 1;
        raf = requestAnimationFrame(wait);
      };
      raf = requestAnimationFrame(wait);
      return () => cancelAnimationFrame(raf);
    }
    const W = cv.width, H = cv.height, PAD = 34;
    // [D-424] шкала від PROF_MIN до PROF_MAX: нуль (оригінал) — не внизу
    const toY = (v) => H - PAD - (H - PAD * 2) * ((v - PROF_MIN) / (PROF_MAX - PROF_MIN));
    const toV = (y) => Math.max(PROF_MIN, Math.min(PROF_MAX,
      PROF_MIN + (H - PAD - y) / (H - PAD * 2) * (PROF_MAX - PROF_MIN)));
    // [D-382] Вісь часу нелінійна: голова й хвіст займають по 30 %
    // ширини, середина 40 %. Інакше сім точок першої трисекундівки
    // злиплись би в 10 % екрана.
    const toX = (i) => {
      const inner = W - PAD * 2;
      if (i < PROF_HEAD) {
        return PAD + inner * 0.30 * i / (PROF_HEAD - 1);
      }
      if (i < PROF_HEAD + PROF_MID) {
        const k = (i - PROF_HEAD + 1) / (PROF_MID + 1);
        return PAD + inner * (0.30 + 0.40 * k);
      }
      const j = i - PROF_HEAD - PROF_MID;
      return PAD + inner * (0.70 + 0.30 * j / (PROF_TAIL - 1));
    };

    const draw = () => {
      const g = cv.getContext('2d');
      if (!g) return;
      g.fillStyle = '#0b0f14'; g.fillRect(0, 0, W, H);
      // сітка й підписи
      g.strokeStyle = 'rgba(168,190,206,.14)'; g.lineWidth = 1;
      g.fillStyle = '#a8bece'; g.font = '11px sans-serif';
      for (let p = PROF_MIN; p <= PROF_MAX; p += 10) {
        const y = toY(p);
        g.beginPath(); g.moveTo(PAD, y); g.lineTo(W - PAD, y); g.stroke();
        g.fillText(`${p > 0 ? '+' : ''}${p}%`, 4, y + 4);
      }
      // [D-424] лінія оригіналу й зона віддалення
      g.fillStyle = 'rgba(255,92,168,.06)';
      g.fillRect(PAD, toY(0), W - PAD * 2, toY(PROF_MIN) - toY(0));
      g.strokeStyle = '#ffd166'; g.lineWidth = 2; g.setLineDash([8, 6]);
      g.beginPath(); g.moveTo(PAD, toY(0)); g.lineTo(W - PAD, toY(0)); g.stroke();
      g.setLineDash([]); g.lineWidth = 1;
      g.fillStyle = '#ffd166'; g.font = 'bold 11px sans-serif';
      g.fillText('оригінал', W - PAD - 58, toY(0) - 6);
      g.fillStyle = '#ff8fab';
      g.fillText('▼ віддалення (краї — розмита копія кадру)', W - PAD - 250, toY(PROF_MIN) - 6);
      g.font = '11px sans-serif'; g.fillStyle = '#a8bece';
      // [D-382] підписи по зонах: у голові й хвості — секунди з
      // кроком 0.5, посередині — просто «середина»
      for (let i = 0; i < PROF_N; i++) {
        const x = toX(i);
        const isEdge = i < PROF_HEAD || i >= PROF_HEAD + PROF_MID;
        g.strokeStyle = isEdge ? 'rgba(77,166,255,.14)' : 'rgba(168,190,206,.08)';
        g.beginPath(); g.moveTo(x, PAD - 8); g.lineTo(x, H - PAD + 8); g.stroke();
        g.fillStyle = '#a8bece';
        if (i < PROF_HEAD) {
          g.fillText(`${(i * 0.5).toFixed(1)}`, x - 8, H - 10);
        } else if (i >= PROF_HEAD + PROF_MID) {
          const j = i - PROF_HEAD - PROF_MID;
          g.fillText(`−${((PROF_TAIL - 1 - j) * 0.5).toFixed(1)}`, x - 10, H - 10);
        }
      }
      // межі зон
      g.fillStyle = '#7ffcd3'; g.font = 'bold 11px sans-serif';
      g.fillText('перші 4 с', toX(0), PAD - 14);
      g.fillText('середина', toX(PROF_HEAD + 1) - 20, PAD - 14);
      g.fillText('останні 3 с', toX(PROF_N - 1) - 60, PAD - 14);
      g.font = '11px sans-serif';
      // реальна крива версії — тонко, позаду
      const pts = (pvData && pvData.points) || [];
      if (pts.length) {
        const dur = pts[pts.length - 1].t || 1;
        const zs = pts.map(p => p.z);
        const lo = Math.min(1, ...zs);
        g.strokeStyle = 'rgba(0,229,160,.35)'; g.lineWidth = 1.5;
        g.beginPath();
        pts.forEach((p, i) => {
          const x = PAD + (W - PAD * 2) * p.t / dur;
          const y = toY((p.z - lo) * 100);
          if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
        });
        g.stroke();
      }
      // сам профіль
      g.strokeStyle = '#4da6ff'; g.lineWidth = 3;
      g.beginPath();
      profVals.forEach((v, i) => {
        const x = toX(i), y = toY(v);
        if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
      });
      g.stroke();
      profVals.forEach((v, i) => {
        const x = toX(i), y = toY(v);
        g.fillStyle = v < 0 ? '#ff5ca8' : '#4da6ff';
        g.beginPath(); g.arc(x, y, 9, 0, Math.PI * 2); g.fill();
        g.fillStyle = '#04121a'; g.font = 'bold 11px monospace';
        const _lbl = v.toFixed(0);
        g.fillText(_lbl, x - _lbl.length * 3.4, y + 4);
      });
    };
    draw();

    const pos = (e) => {
      const r = cv.getBoundingClientRect();
      const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      const cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
      return { x: cx * cv.width / r.width, y: cy * cv.height / r.height };
    };
    const down = (e) => {
      const { x, y } = pos(e);
      let best = -1, bd = 1e9;
      profVals.forEach((v, i) => {
        const d = Math.hypot(toX(i) - x, toY(v) - y);
        if (d < bd) { bd = d; best = i; }
      });
      if (bd < 40) { profDrag.current = best; move(e); }
    };
    const move = (e) => {
      if (profDrag.current < 0) return;
      e.preventDefault();
      const { y } = pos(e);
      const next = [...profVals];
      next[profDrag.current] = toV(y);
      saveProf(next);
      // [D-426] показати в прев'ю саме ту секунду, яку тягнемо
      {
        const i = profDrag.current, D = Number(pvDur) || 30;
        const tt = i < PROF_HEAD ? i * 0.5
          : i >= PROF_HEAD + PROF_MID ? D - (PROF_N - 1 - i) * 0.5
          : 4 + (D - 7) * (i - PROF_HEAD + 1) / (PROF_MID + 1);
        setHcFocus(f => ({ keys: [], t: Math.max(0, Math.min(D, tt)), n: f.n + 1 }));
      }
    };
    const up = () => { profDrag.current = -1; };
    cv.addEventListener('mousedown', down);
    cv.addEventListener('touchstart', down, { passive: false });
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      cv.removeEventListener('mousedown', down);
      cv.removeEventListener('touchstart', down);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, [profOpen, profVals, pvData, pvSeed, profTick]);

  // [D-378] перетягування вікна діаграми за заголовок
  useEffect(() => {
    if (!profOpen) return undefined;
    const move = (e) => {
      const d = dlgDrag.current;
      if (!d) return;
      setDlgPos({ x: d.ox + (e.clientX - d.sx), y: d.oy + (e.clientY - d.sy) });
    };
    const up = () => { dlgDrag.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [profOpen]);

  const save = (patch) => {
    setS(prev => ({ ...prev, ...patch }));
    setHcFocus(f => ({ keys: Object.keys(patch || {}), n: f.n + 1 }));
    api.post('/referral/settings', patch)
      .then(() => setToast('Збережено'))
      .catch(e => setErr(e?.response?.data?.detail || 'Помилка збереження'));
  };

  const savePay = (patch) => {
    setPay(prev => ({ ...prev, ...patch }));
    api.post('/media/payment-config', patch)
      .then(() => { setToast('Збережено'); loadPay(); })
      .catch(e => setErr(e?.response?.data?.detail || 'Помилка збереження'));
  };

  const saveMono = (on) => {
    api.post('/media/mono/config', { enabled: on })
      .then(() => { setToast('Збережено'); loadPay(); })
      .catch(e => setErr(e?.response?.data?.detail || 'Помилка збереження'));
  };

  if (!s) return <Box sx={{ p: 3 }}><Typography sx={{ color: C.dim }}>Завантаження…</Typography></Box>;

  const price = Number(s.uniq_hyper_price_tokens || 0);
  const defVer = Math.max(1, Number(s.uniq_hyper_versions_default || 3));
  const maxVer = Math.max(1, Number(s.uniq_hyper_versions_max || 10));
  const onFlag = (k) => String(s[k] ?? '1') === '1';


  const tgStyle = {
    flex: 1, textTransform: 'none', fontWeight: 700, fontSize: 13.5,
    color: C.dim, borderColor: 'rgba(255,255,255,.12)',
    '&.Mui-selected': { bgcolor: 'rgba(0,229,160,.18)', color: C.neonTxt,
                        '&:hover': { bgcolor: 'rgba(0,229,160,.24)' } },
  };

  const priceField = (key, def) => (
    <TextField
      type="number" size="small" fullWidth
      value={Number(s[key] ?? def)}
      onChange={e => save({ [key]: String(Math.max(0, Number(e.target.value) || 0)) })}
      inputProps={{ min: 0, max: 10000,
                    style: { color: C.txt, fontSize: 19, fontWeight: 800 } }}
      sx={{
        '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,.05)' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line },
      }}
    />
  );

  // рядок «метод оплати — увімкнено/вимкнено»
  const payRow = (label, hint, on, onToggle) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1,
               borderBottom: '1px solid rgba(255,255,255,.06)' }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 14, color: C.txt }}>{label}</Typography>
        {hint && <Typography sx={{ fontSize: 11.5, color: C.dim }}>{hint}</Typography>}
      </Box>
      <Button size="small" variant={on ? 'contained' : 'outlined'} onClick={onToggle}
        sx={{ minWidth: 104, fontWeight: 700, textTransform: 'none',
              bgcolor: on ? 'rgba(0,229,160,.9)' : 'transparent',
              color: on ? '#06231a' : C.dim,
              borderColor: on ? C.neon : 'rgba(255,255,255,.18)',
              '&:hover': { bgcolor: on ? C.neon : 'rgba(255,255,255,.05)' } }}>
        {on ? '🟢 Показуємо' : '⚪ Приховано'}
      </Button>
    </Box>
  );

  return (
    // [D-359] 🔴 Тут стояло глухе `maxWidth: 700`. На моніторі 1920 це
    // 36 % ширини — і будь-які «дві колонки» всередині ділили саме цю
    // смужку, лишаючи пів екрана порожнім. Для «Монтажу», де ручок
    // багато, обмеження знято; для решти лишається — там суцільний
    // текст, і надто довгі рядки читати гірше.
    // [D-373] На «Монтажі» сторінка — це два стовпці: ліворуч усе
    // разом із заголовком, праворуч прев'ю від самого верху. Поки
    // прев'ю жило всередині вкладки, воно починалось нижче заголовка,
    // вкладок і смуги — і крива не влазила в екран.
    <Box sx={{ p: { xs: 2, md: 3 },
               // [D-426] права колонка з прев'ю ДО/ПІСЛЯ — ще й на «Ефектах» і «Мікро-ефектах»
               maxWidth: tab === 'dir' ? 1900 : (['fx', 'micro'].includes(tab) ? 1240 : 700),
               display: ['dir', 'fx', 'micro'].includes(tab) ? 'flex' : 'block',
               gap: 2.5, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: ['fx', 'micro'].includes(tab) ? 720 : 'none' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: .3, color: C.txt }}>
        🧪 Посилення
      </Typography>
      <Typography sx={{ fontSize: 13, mb: 2, color: C.dim }}>
        Ефекти, переклад озвучки, ціни в токенах і способи оплати
      </Typography>

      <Tabs
        value={tab} onChange={(e, v) => setTabSafe(v)} variant="scrollable"
        scrollButtons="auto" allowScrollButtonsMobile
        sx={{
          mb: 2, minHeight: 40,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13.5,
                              color: C.dim, minHeight: 40, px: 1.8 },
          '& .Mui-selected': { color: C.neonTxt + ' !important' },
          '& .MuiTabs-indicator': { backgroundColor: C.neon, height: 3, borderRadius: 2 },
        }}>
        {TABS.map(t => <Tab key={t.k} value={t.k} label={t.t} />)}
      </Tabs>

      {/* ══════════ ВКЛАДКА 1 — ефекти й ціна ══════════ */}
      {tab === 'fx' && (
        <>
          <Card ui={cardFold} title="Кому доступно"
                chip={MODES.find(m => m.v === (s.uniq_hyper_mode || 'off'))?.t}>
            <ToggleButtonGroup exclusive size="small" fullWidth
              value={s.uniq_hyper_mode || 'off'}
              onChange={(e, v) => v && save({ uniq_hyper_mode: v })}>
              {MODES.map(m => (
                <ToggleButton key={m.v} value={m.v} sx={tgStyle}>{m.t}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Card>

          <Card ui={cardFold} title="Ціна за запуск" chip={price === 0 ? 'безкоштовно' : `${price} ток.`}
                chipColor={price === 0 ? 'neon' : 'coral'}>
            {priceField('uniq_hyper_price_tokens', 0)}
            <Typography sx={{ fontSize: 12, color: C.dim, mt: .8 }}>
              Списується при натисканні «Запустити», одним платежем разом із перекладом.
              0 = безкоштовно.
            </Typography>
          </Card>

          {/* [D-512] AI-обкладинка Студії: рушій, ціна, лічильники для звірки з рахунком */}
          <Card ui={cardFold} title="🤖 AI-обкладинка (Студія і ПК)"
                chip={`${(s.uniq_cover_ai_engine || 'gemini') === 'openai' ? 'GPT Image' : 'nano-banana'} · ${Number(s.uniq_cover_ai_price_tokens || 0)} ток.`}
                chipColor={Number(s.uniq_cover_ai_price_tokens || 0) === 0 ? 'coral' : 'neon'}>
            <ToggleButtonGroup exclusive size="small" fullWidth value={s.uniq_cover_ai_engine || 'gemini'}
              onChange={(e, v) => v && save({ uniq_cover_ai_engine: v })}>
              <ToggleButton value="gemini" sx={tgStyle}>🍌 nano-banana (запасний)</ToggleButton>
              <ToggleButton value="openai" sx={tgStyle}>🎨 GPT Image (OpenAI)</ToggleButton>
            </ToggleButtonGroup>
            {/* [D-514] OWNER: «це меню треба прибрати бо по замовченю… Зберігати точно, Справжні люди поверх AI-фону, фото-зразок» — завжди */}
            <Typography sx={{ fontSize: 11.5, color: C.dim, mt: .8 }}>
              Завжди: AI сам компонує кіноафішу (герої великим планом) · фото-зразки облич · «зберігати обличчя точно».
              Назву малюємо самі (без помилок у літерах), у відео — з рухом. nano-banana — запасний рушій.
              {/* [D-515] вклеювання справжніх людей вимкнено — давало подвійне зображення (тест OWNER 09.10) */}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: C.dim, mt: 1, mb: .5 }}>Ціна для учня, токенів за одну обкладинку:</Typography>
            {priceField('uniq_cover_ai_price_tokens', 0)}
            <Typography sx={{ fontSize: 12, color: C.dim, mt: .8 }}>
              Згенеровано: nano-banana — <b style={{ color: C.txt }}>{Number(s.uniq_cover_ai_count_gemini || 0)}</b>,
              GPT Image — <b style={{ color: C.txt }}>{Number(s.uniq_cover_ai_count_openai || 0)}</b>.
              Як звірити: запишіть рахунок у Google AI Studio / OpenAI → зробіть кілька обкладинок → різниця ÷ кількість = собівартість однієї.
              Орієнтовно: nano-banana ≈ $0,04, GPT Image (medium) ≈ $0,06. 0 токенів = безкоштовно (лише поки функція схована від партнерів).
              Не вийшло — токени повертаються учню автоматично.
            </Typography>
          </Card>

          {/* [D-321] Ліміти версій: скільки юзер може замовити за раз */}
          <Card ui={cardFold} title="Версій за раз" chip={`${defVer} за замовч. · до ${maxVer}`}>
            <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>
              За замовчуванням для всіх
            </Typography>
            <Slider
              value={Math.min(defVer, maxVer)} min={1} max={maxVer} step={1} marks
              valueLabelDisplay="auto"
              onChangeCommitted={(e, v) => save({ uniq_hyper_versions_default: String(v) })}
              sx={sliderSx}
            />
            <Typography sx={{ fontSize: 12.5, color: C.dim, mt: 2, mb: .5 }}>
              Максимум, який можна тягнути
            </Typography>
            <Slider
              value={maxVer} min={1} max={30} step={1} marks={[1, 10, 20, 30].map(v => ({ value: v, label: String(v) }))}
              valueLabelDisplay="auto"
              onChangeCommitted={(e, v) => save({ uniq_hyper_versions_max: String(v) })}
              sx={sliderSx}
            />
            <Typography sx={{ fontSize: 11.5, color: C.dim, mt: 1 }}>
              Окремий ліміт конкретному юзеру — у картці користувача
            </Typography>
          </Card>

          {/* [D-407] Кнопки застосунку: що юзер бачить і як воно стоїть
              спочатку. Список розширено з трьох до всіх функцій.

              🔴 Окремого ДРУГОГО місця керування навмисно не заводимо:
              три перші ключі тут ті самі, що були до D-407. Двоє місць
              запису на одне значення — це те, як три тижні жив BUG-115.

              🔴 Приховано ≠ вимкнено (рішення OWNER): схована кнопка
              просто не видна юзеру, а працює за тим, що стоїть праворуч. */}
          <Card ui={cardFold} title="Кнопки в застосунку">
            {feats.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 70px 78px',
                           alignItems: 'center', pb: .9, mb: .3,
                           borderBottom: `1px solid ${C.line}` }}>
                  <Typography sx={{ fontSize: 11.5, color: C.dim }}>Функція</Typography>
                  <Typography sx={{ fontSize: 11.5, color: C.dim, textAlign: 'center' }}>
                    видно
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: C.dim, textAlign: 'center' }}>
                    спочатку
                  </Typography>
                </Box>
                {feats.map(f => {
                  const kv = 'uniq_hyper_' + f.key;
                  const kx = 'uniq_hyper_vis_' + f.key;
                  const cur = s || {};
                  // ключа ще немає в базі → дефолт з довідника, а не '1'
                  const vis = cur[kx] === undefined
                    ? !!f.visible_default : String(cur[kx]) === '1';
                  const val = cur[kv] === undefined
                    ? !!f.value_default : String(cur[kv]) === '1';
                  return (
                    <Box key={f.key}
                         sx={{ display: 'grid', gridTemplateColumns: '1fr 70px 78px',
                               alignItems: 'center', py: .35 }}>
                      <Box sx={{ minWidth: 0, pr: 1 }}>
                        <Typography sx={{ fontSize: 14, color: vis ? C.txt : C.dim }}>
                          {f.title}
                        </Typography>
                        {f.descr ? (
                          <Typography sx={{ fontSize: 11, color: C.dim, lineHeight: 1.3 }}>
                            {f.descr}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Switch size="small" checked={vis} sx={swSx}
                          onChange={e => save({ [kx]: e.target.checked ? '1' : '0' })} />
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Switch size="small" checked={val} sx={swSx}
                          onChange={e => save({ [kv]: e.target.checked ? '1' : '0' })} />
                      </Box>
                    </Box>
                  );
                })}
                <Typography sx={{ fontSize: 11.5, color: C.dim, mt: 1.4,
                                  lineHeight: 1.45 }}>
                  <b style={{ color: C.neonTxt }}>видно</b> — чи бачить юзер кнопку.
                  <b style={{ color: C.neonTxt }}> спочатку</b> — як вона стоїть,
                  коли юзер відкриває застосунок.<br />
                  Схована кнопка не зникає з роботи: вона працює так, як стоїть
                  праворуч, просто юзер її не міняє.
                </Typography>
              </Box>
            ) : (
              /* Довідник не завантажився — лишаємо старий список, щоб
                 керування трьома основними ефектами не зникало ніколи. */
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {[
                  ['uniq_hyper_bg', 'Фон — розмиття або заміна'],
                  ['uniq_hyper_author', 'Автор окремим шаром у кадрі'],
                  ['uniq_hyper_subs', 'Субтитри — різні в кожній версії'],
                ].map(([k, label]) => (
                  <FormControlLabel key={k}
                    control={
                      <Switch checked={onFlag(k)} sx={swSx}
                        onChange={e => save({ [k]: e.target.checked ? '1' : '0' })} />
                    }
                    label={<Typography sx={{ fontSize: 14, color: C.txt }}>{label}</Typography>}
                  />
                ))}
              </Box>
            )}
          </Card>

          <Typography sx={{ fontSize: 12, color: C.dim }}>
            Обробка довша на ~1.5 хв · собівартість ефектів ≈ 0.0005 € за ролик
          </Typography>
        </>
      )}

      {/* ══════════ ВКЛАДКА 2 — переклад озвучки ══════════ */}

      {tab === 'dir' && (
        <>
        {/* [D-358] Дві колонки: ручки ліворуч, прев'ю праворуч і
            закріплене. Праворуч у дашборді половина екрана стояла
            порожня, а прев'ю жило в окремій вкладці — міняєш ручку й
            не бачиш результату. */}
        {/* TOPBAR [D-371] Версія, приклад, набори й теми — не про
            окремий розділ, а про весь екран. Тому зверху й нерухомо, а
            не всередині закріпленої картки прев'ю. */}
        <Paper elevation={0} sx={{
          p: 1.5, mb: 2, borderRadius: 3, bgcolor: C.card,
          border: `1px solid ${C.line}`, backgroundImage: 'none',
          display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
            <Typography sx={{ fontSize: 12, color: C.dim }}>Версія</Typography>
            {[0, 1, 2, 3].map(sd => (
              <Button key={sd} size="small"
                variant={pvSeed === sd ? 'contained' : 'outlined'}
                onClick={() => setPvSeed(sd)}
                sx={{ minWidth: 38, px: 0, fontSize: 11.5,
                      ...(pvSeed === sd ? { bgcolor: C.neon, color: '#04121a' }
                                        : { borderColor: C.line, color: C.dim }) }}>
                в{sd + 1}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
            <Typography sx={{ fontSize: 12, color: C.dim }}>Приклад</Typography>
            {[15, 30, 60].map(d => (
              <Button key={d} size="small"
                variant={pvDur === d ? 'contained' : 'outlined'}
                onClick={() => { setPvDur(d); setPvTime(0); }}
                sx={{ minWidth: 38, px: 0, fontSize: 11.5,
                      ...(pvDur === d ? { bgcolor: C.neon, color: '#04121a' }
                                      : { borderColor: C.line, color: C.dim }) }}>
                {d}с
              </Button>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
            <Typography sx={{ fontSize: 12, color: C.dim }}>Набір</Typography>
            {PRESETS.map(pr => (
              <Button key={pr.k} size="small"
                variant={activePreset === pr.k ? 'contained' : 'outlined'}
                onClick={() => save(pr.v)}
                sx={{ textTransform: 'none', fontSize: 11.5,
                      ...(activePreset === pr.k
                        ? { bgcolor: C.neon, color: '#04121a' }
                        : { borderColor: C.line, color: C.dim }) }}>
                {pr.t}
              </Button>
            ))}
            {!activePreset && (
              <Typography sx={{ fontSize: 11.5, color: C.coral }}>своє</Typography>
            )}
          </Box>

          {/* [D-533] OWNER 11.10: «не бачу куди у нас ділося створення нової теми і дуже не зручно показуються створені теми» —
              теми стояли в одному рядку без переносу, і поле «нова тема» виїжджало за край екрана. Тепер теми — окремим блоком на
              всю ширину: створення зверху й завжди видно, теми — сіткою. */}
          <Box data-themes="box" sx={{ flexBasis: '100%', minWidth: 0, pt: 1.2, borderTop: `1px solid ${C.line}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: C.txt }}>🎨 Теми · {themes.length} з {THEME_SLOTS}</Typography>
              <Typography sx={{ fontSize: 11.5, color: C.dim, flex: 1, minWidth: 220 }}>
                Натисніть тему — її рух підставиться в повзунки. 🧩 — що тема вмикає. × — видалити.
              </Typography>
              <Box data-themes="new" sx={{ display: 'flex', alignItems: 'center', gap: .6, p: .6, borderRadius: 2,
                                           border: `1px solid ${C.neon}`, bgcolor: 'rgba(0,229,160,.07)' }}>
                <Typography sx={{ fontSize: 12, color: C.neon, fontWeight: 700, pl: .4 }}>➕ Нова тема</Typography>
                <TextField size="small" placeholder="назва (до 20 символів)" value={themeName}
                  onChange={e => setThemeName(e.target.value.slice(0, 20))}
                  onKeyDown={e => e.key === 'Enter' && themeName.trim() && saveTheme()}
                  sx={{ width: 200,
                        '& .MuiInputBase-root': { color: C.txt, fontSize: 12.5 },
                        '& fieldset': { borderColor: C.line } }} />
                <Button size="small" variant="contained" onClick={saveTheme}
                  disabled={!themeName.trim()}
                  title="Зберегти поточні повзунки й криву як тему. Назва наявної теми — оновить її."
                  sx={{ fontSize: 11.5, textTransform: 'none', fontWeight: 700, bgcolor: C.neon, color: '#04121a',
                        '&:hover': { bgcolor: C.neon } }}>
                  💾 Зберегти поточний рух
                </Button>
              </Box>
            </Box>
            {themes.length === 0 && <Typography sx={{ fontSize: 12, color: C.dim }}>Тем ще немає — виставте повзунки, впишіть назву й збережіть.</Typography>}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: .7 }}>
              {themes.map(th => (
                <Box key={th.slot} data-themes="card" sx={{ display: 'flex', alignItems: 'stretch', borderRadius: 2, overflow: 'hidden',
                                        border: `1px solid ${C.line}`, bgcolor: 'rgba(255,255,255,.03)' }}>
                  <Button size="small" onClick={() => save(th.v)}
                    title={(th.v.uniq_dir_profile ? 'зі своєю кривою руху' : 'без власної кривої') + ' — натисніть, щоб підставити в повзунки'}
                    sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textTransform: 'none', fontSize: 12.5, fontWeight: 700,
                          color: C.txt, borderRadius: 0, px: 1 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {th.v.uniq_dir_profile ? '📈 ' : '🎥 '}{th.n}</span>
                  </Button>
                  <Button size="small" onClick={() => setThemeName(th.n)} title="підставити назву, щоб оновити цю тему поточними повзунками"
                    sx={{ minWidth: 30, px: .3, fontSize: 12, color: C.dim, borderRadius: 0, borderLeft: `1px solid ${C.line}` }}>♻️</Button>
                  <Button size="small" onClick={() => openThFx(th)} title="набір функцій теми (для «Студії» учнів партнера)"
                    sx={{ minWidth: 34, px: .3, fontSize: 11.5, color: thFxCount(th.slot) ? C.neon : C.dim,
                          borderRadius: 0, borderLeft: `1px solid ${C.line}` }}>🧩{thFxCount(th.slot) || ''}</Button>   {/* [D-474] */}
                  <Button size="small" onClick={() => delTheme(th.slot)} title="видалити тему в усіх"
                    sx={{ minWidth: 28, px: 0, fontSize: 14, color: C.coral, borderRadius: 0, borderLeft: `1px solid ${C.line}` }}>×</Button>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

          {/* [D-359] CSS-колонки замість одного стовпчика: на широкому
              екрані картки лягають у два ряди й усе робоче видно без
              прокрутки. Це masonry без жодної бібліотеки. */}
          {/* [D-363] Grid, а не columnCount: перестановка зроблена через
              CSS `order`, а в багатоколонковій розкладці він не діє. */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex',
                     flexDirection: 'column',
                     gap: 0 }}>
          {/* [D-370] Розділи — кнопками. Одна картка за раз, прев'ю
              праворуч завжди видиме: саме цього не вистачало, коли
              ручки стояли довгим стовпчиком і до прев'ю треба було
              вертатись. */}
          <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap', mb: 2 }}>
            <Button size="small" key="s0"
              variant={section === 's0' ? 'contained' : 'outlined'}
              onClick={() => setSection(section === 's0' ? '' : 's0')}
              sx={{ textTransform: 'none', fontSize: 12.5,
                    ...(section === 's0'
                      ? { bgcolor: C.neon, color: '#04121a' }
                      : { borderColor: C.line, color: C.txt }) }}>
              🖼 Ілюстрації
            </Button>
            <Button size="small" key="s1"
              variant={section === 's1' ? 'contained' : 'outlined'}
              onClick={() => setSection(section === 's1' ? '' : 's1')}
              sx={{ textTransform: 'none', fontSize: 12.5,
                    ...(section === 's1'
                      ? { bgcolor: C.neon, color: '#04121a' }
                      : { borderColor: C.line, color: C.txt }) }}>
              🎨 Фон
            </Button>
            <Button size="small" key="s2"
              variant={section === 's2' ? 'contained' : 'outlined'}
              onClick={() => setSection(section === 's2' ? '' : 's2')}
              sx={{ textTransform: 'none', fontSize: 12.5,
                    ...(section === 's2'
                      ? { bgcolor: C.neon, color: '#04121a' }
                      : { borderColor: C.line, color: C.txt }) }}>
              🎬 Режисер
            </Button>
            <Button size="small" key="s3"
              variant={section === 's3' ? 'contained' : 'outlined'}
              onClick={() => setSection(section === 's3' ? '' : 's3')}
              sx={{ textTransform: 'none', fontSize: 12.5,
                    ...(section === 's3'
                      ? { bgcolor: C.neon, color: '#04121a' }
                      : { borderColor: C.line, color: C.txt }) }}>
              ▶ Вступ
            </Button>
            <Button size="small" key="s4"
              variant={section === 's4' ? 'contained' : 'outlined'}
              onClick={() => setSection(section === 's4' ? '' : 's4')}
              sx={{ textTransform: 'none', fontSize: 12.5,
                    ...(section === 's4'
                      ? { bgcolor: C.neon, color: '#04121a' }
                      : { borderColor: C.line, color: C.txt }) }}>
              🔍 Зум у сцені
            </Button>
            <Button size="small" key="s5"
              variant={section === 's5' ? 'contained' : 'outlined'}
              onClick={() => setSection(section === 's5' ? '' : 's5')}
              sx={{ textTransform: 'none', fontSize: 12.5,
                    ...(section === 's5'
                      ? { bgcolor: C.neon, color: '#04121a' }
                      : { borderColor: C.line, color: C.txt }) }}>
              📍 Скільки і де
            </Button>
            <Button size="small" key="s6"
              variant={section === 's6' ? 'contained' : 'outlined'}
              onClick={() => setSection(section === 's6' ? '' : 's6')}
              sx={{ textTransform: 'none', fontSize: 12.5,
                    ...(section === 's6'
                      ? { bgcolor: C.neon, color: '#04121a' }
                      : { borderColor: C.line, color: C.txt }) }}>
              ✂️ Маска
            </Button>
            <Button size="small" key="s7"
              variant={section === 's7' ? 'contained' : 'outlined'}
              onClick={() => setSection(section === 's7' ? '' : 's7')}
              sx={{ textTransform: 'none', fontSize: 12.5,
                    ...(section === 's7'
                      ? { bgcolor: C.neon, color: '#04121a' }
                      : { borderColor: C.line, color: C.txt }) }}>
              ❔ Довідка
            </Button>
          </Box>

          {section === 's0' && (
<Card title="Ілюстрації: дефолт"
                chip={String(s.uniq_broll_cards || '1') === '1' ? 'картки' : 'врізка'}
                chipColor="neon">
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_broll_cards || '1') === '1'}
                  onChange={e => save({ uniq_broll_cards: e.target.checked ? '1' : '0' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Показувати ілюстрації стосом карток
              </Typography>}
            />
            <Typography sx={{ fontSize: 12, color: C.dim, mt: .5, mb: 1.5, lineHeight: 1.5 }}>
              Як у прикладі-орієнтирі: кілька фото в білих рамках віялом під
              автором. Вимкнено — одна врізка частиною кадру, як було.
            </Typography>
          </Card>
          )}
          {section === 's1' && (
<Card title="Фон-градієнт: дефолт"
                chip={String(s.uniq_bg_gradient || '0') === '1' ? 'увімкнено' : 'вимкнено'}
                chipColor={String(s.uniq_bg_gradient || '0') === '1' ? 'neon' : 'coral'}>
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_bg_gradient || '0') === '1'}
                  onChange={e => save({ uniq_bg_gradient: e.target.checked ? '1' : '0' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Вирізати автора й ставити на кольоровий градієнт
              </Typography>}
            />
            <Note>
              Як у прикладі-орієнтирі: автор на чистому кольорі замість свого
              приміщення. П'ять палітр, у кожної версії своя.<br />
              Працює лише якщо юзер НЕ додав власних картинок — його матеріал
              важливіший.<br />
              Маска тут рахується суворішою моделлю (тільки людина): на чистому
              фоні зайвий предмет із кадру висить у повітрі, на розмитті його
              не видно.
            </Note>
          </Card>
          )}
          {section === 's2' && (
<Card title="Режисер: дефолт"
                chip={String(s.uniq_dir_enabled || '1') === '1' ? 'увімкнено' : 'вимкнено'}
                chipColor={String(s.uniq_dir_enabled || '1') === '1' ? 'neon' : 'coral'}>
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_dir_enabled || '1') === '1'}
                  onChange={e => save({ uniq_dir_enabled: e.target.checked ? '1' : '0' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Міняти крупність кадру по ходу ролика
              </Typography>}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
              {[
                ['uniq_dir_pace', 'Ритм: середній план, с', 4],
                ['uniq_dir_strength', 'Сила наїзду, %', 100],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Note>
              <b>Ритм</b> — як часто міняється план. 4&nbsp;с дає ~17 планів на
              хвилину, 6&nbsp;с — спокійніше, 2.5&nbsp;с — різко.<br />
              <b>Сила</b> — наскільки крупний план відрізняється від загального.
              100% — як зараз, 50% — удвічі делікатніше, 0% — крупність не
              міняється зовсім.<br />
              Працює лише при повнокадровій подачі: на врізку автора наїзд не
              роблять, там своя композиція. Додає ~90&nbsp;с обробки на версію.
            </Note>
          </Card>
          )}
          {section === 's3' && (
<Card title="Вступний рух: перші секунди"
                chip={String(s.uniq_dir_intro_on || '1') === '1'
                      ? `${Number(s.uniq_dir_intro_amp || 3)}% за ${Number(s.uniq_dir_intro_dur || 1.5)} с`
                      : 'вимкнено'}
                chipColor={String(s.uniq_dir_intro_on || '1') === '1' ? 'neon' : 'coral'}>
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_dir_intro_on || '1') === '1'}
                  onChange={e => save({ uniq_dir_intro_on: e.target.checked ? '1' : '0' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Починати кадр не з оригінального розміру
              </Typography>}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
              {[
                ['uniq_dir_intro_amp', 'Наскільки відхилитись, %', 3],
                ['uniq_dir_intro_dur', 'Темп: приїзд за, с', 1.5],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Note>
              Раніше ролик стартував рівно з оригінального кадру, і перша зміна
              приходила аж на 5-й секунді. Тепер кадр «приїжджає» до свого
              розміру з першого ж кадру: у половини версій автор трохи менший і
              виростає, у половини — більший і зменшується.<br />
              <b>Відхилення</b> — 3% оку не читається, але для порівняння відео
              перші секунди вже інші. 1-2% ледь-ледь, 6% помітно як наїзд.<br />
              <b>Темп</b> — за скільки секунд кадр стане на місце. 1.5&nbsp;с
              плавно, 0.6&nbsp;с — підкреслено, 3&nbsp;с — майже непомітно.<br />
              Кожна версія бере свої числа в межах ±35% від заданих, щоб копії
              не стартували однаково.<br />
              🔴 Ціна: ролик іде з постійним запасом зуму 4%. Кадр не можна
              показати меншим за рамку, тож без запасу «автор виростає» було б
              неможливе. Якщо поставити відхилення більше за 4%, запас
              підніметься рівно настільки, скільки треба — заявлене
              відхилення ви отримаєте завжди, але кадр буде підрізаний сильніше.<br />
              Працює разом із «Режисером» — окремо не вмикається.
            </Note>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch checked={String(s.uniq_dir_intro_solo || '0') === '1'}
                  onChange={e => save({ uniq_dir_intro_solo: e.target.checked ? '1' : '0' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Робити вступ навіть коли «Зміна планів» вимкнена
              </Typography>}
            />
            <Note>
              🔴 Вимкнено за замовчуванням, і це навмисно. Вступ виконується
              окремим проходом обробки — <b>близько 90 с на версію</b>. Хто
              вимкнув «Зміну планів», на цю ціну не підписувався. Увімкніть,
              якщо хочете рух на початку в усіх замовленнях без винятку.
            </Note>
            <Button variant="outlined" size="small" sx={{ mt: 1.5, borderColor: C.neon, color: C.neon }}
              onClick={() => save({ uniq_dir_intro_on: '1', uniq_dir_intro_amp: '3', uniq_dir_intro_dur: '1.5', uniq_dir_intro_solo: '0' })}>
              Скинути до норми
            </Button>
          </Card>
          )}
          {section === 's4' && (
<Card title="Зум усередині сцени"
                chip={String(s.uniq_dir_scene_on || '1') === '1'
                      ? `${Number(s.uniq_dir_scene_moves_min || 1)}-${Number(s.uniq_dir_scene_moves_max || 3)} ходи · до ${Number(s.uniq_dir_scene_speed_max || 40)} %/с`
                      : 'вимкнено'}
                chipColor={String(s.uniq_dir_scene_on || '1') === '1' ? 'neon' : 'coral'}>
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_dir_scene_on || '1') === '1'}
                  onChange={e => save({ uniq_dir_scene_on: e.target.checked ? '1' : '0' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Камера повільно наїжджає й віддаляється всередині плану
              </Typography>}
            />
            <Note>
              Зняв мірку з вашого орієнтира — виміряв розмір голови на кожному
              кадрі. Там камера не стоїть: 0.0-0.6&nbsp;с віддалення на −7.5&nbsp;%
              (швидкість 15&nbsp;%/с), далі наїзд назад +12&nbsp;% (40&nbsp;%/с), а
              перед самим монтажним стиком різкий ривок +58&nbsp;% за 0.2&nbsp;с.
              Тобто повільний дрейф на кілька відсотків увесь час, і різкий
              наїзд на стику. Різкий наїзд у нас уже був (перельот на стику),
              бракувало саме дрейфу.
            </Note>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                ['uniq_dir_scene_moves_min', 'Ходів у сцені: від', 1],
                ['uniq_dir_scene_moves_max', 'до', 3],
                ['uniq_dir_scene_speed_max', 'Швидкість, макс %/с', 40],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12.5, color: C.neon, mt: 2, mb: .5 }}>
              Де зум стоїть на ПОЧАТКУ сцени, %
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                ['uniq_dir_scene_start_min', 'від', -5],
                ['uniq_dir_scene_start_max', 'до', 5],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12.5, color: C.neon, mt: 2, mb: .5 }}>
              Де він опиняється в КІНЦІ сцени, %
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                ['uniq_dir_scene_end_min', 'від', -5],
                ['uniq_dir_scene_end_max', 'до', 5],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Note>
              <b>Ходи</b> — скільки разів камера змінює напрямок усередині
              одного плану. 1 — просто їде від початкового значення до
              кінцевого. 3 — дихає туди-сюди.<br />
              <b>Початок і кінець</b> задаються окремо, і цим ставиться
              напрямок. Однакові межі (−5…+5 обидва) — напрямок випадковий на
              кожну сцену. Початок <b>плюсовий</b>, кінець <b>мінусовий</b> —
              автор завжди починає більшим і зменшується до нормального, як ви
              й просили. Навпаки — завжди наїзд.<br />
              <b>Швидкість</b> — стеля, різкіше камера не поїде. Замір: при
              стелі 8 виходить 7.4&nbsp;%/с, при 40 — 11.7&nbsp;%/с (вище 40
              вона вже нічого не обмежує, бо природний темп ≈12&nbsp;%/с).<br />
              🔴 <b>Усе розводиться по версіях.</b> Ви робите з одного
              оригіналу по 20-50 копій, тож діапазон ділиться на стільки смуг,
              скільки версій, і кожна бере зі своєї — інакше більшість
              скупчилась би навколо середини. Замір на 50 версіях: 1225 пар,
              <b> жодної однакової</b>, найменша різниця кривих 0.011.
            </Note>
            <Button variant="outlined" size="small" sx={{ mt: 1.5, borderColor: C.neon, color: C.neon }}
              onClick={() => save({ uniq_dir_scene_on: '1', uniq_dir_scene_moves_min: '1', uniq_dir_scene_moves_max: '3', uniq_dir_scene_start_min: '-5', uniq_dir_scene_start_max: '5', uniq_dir_scene_end_min: '-5', uniq_dir_scene_end_max: '5', uniq_dir_scene_speed_max: '40' })}>
              Скинути до норми
            </Button>
          </Card>
          )}
          {section === 's5' && (
<Card title="Скільки ефектів і де саме"
                chip={`${String(s.uniq_dir_scene_events || '0') === '0'
                        ? 'у кожній сцені'
                        : s.uniq_dir_scene_events + ' на ролик'} · ${
                        ['по всьому', 'на початку', 'всередині', 'у кінці'][
                          Number(s.uniq_dir_scene_zone || 0)] }`}
                chipColor="neon">
            <Note>
              Ролик ділиться на сцени, і рух камери може лягти не в кожну.
              <b> «1 раз» — це один ефект на весь ролик</b>, а не один у кожній
              сцені. Саме тут прибирається «забагато»: було по руху в кожній із
              50 сцен.
            </Note>
            <Typography sx={{ fontSize: 12.5, color: C.neon, mb: .8 }}>
              Скільки разів за ролик
            </Typography>
            <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap', mb: 2 }}>
              {[['1', '1 раз'], ['2', '2 рази'], ['3', '3 рази'],
                ['5', '5 разів'], ['8', '8 разів'],
                ['0', 'у кожній сцені']].map(([v, lbl]) => (
                <Button key={v} size="small"
                  variant={String(s.uniq_dir_scene_events || '0') === v ? 'contained' : 'outlined'}
                  onClick={() => save({ uniq_dir_scene_events: v })}
                  sx={{ fontSize: 11.5, textTransform: 'none',
                        ...(String(s.uniq_dir_scene_events || '0') === v
                          ? { bgcolor: C.neon, color: '#04121a' }
                          : { borderColor: C.line, color: C.dim }) }}>
                  {lbl}
                </Button>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12.5, color: C.neon, mb: .8 }}>
              У якій частині ролика
            </Typography>
            <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap' }}>
              {[['0', 'по всьому'], ['1', 'на початку'],
                ['2', 'всередині'], ['3', 'у кінці']].map(([v, lbl]) => (
                <Button key={v} size="small"
                  variant={String(s.uniq_dir_scene_zone || '0') === v ? 'contained' : 'outlined'}
                  onClick={() => save({ uniq_dir_scene_zone: v })}
                  sx={{ fontSize: 11.5, textTransform: 'none',
                        ...(String(s.uniq_dir_scene_zone || '0') === v
                          ? { bgcolor: C.neon, color: '#04121a' }
                          : { borderColor: C.line, color: C.dim }) }}>
                  {lbl}
                </Button>
              ))}
            </Box>
            <Note>
              Сцени в межах частини беруться рівномірно: три ефекти «по всьому»
              ляжуть на початку, всередині й у кінці, а не злипнуться в одному
              місці. Кожна версія бере свої сцени — копії не збігаються.
            </Note>
          </Card>
          )}
          {section === 's6' && (<>
<Card title="Ефектні титри й маска в Студії"
                chip={String(s.uniq_studio_pop_off ?? '1') === '0' ? 'увімкнено' : 'вимкнено'}
                chipColor={String(s.uniq_studio_pop_off ?? '1') === '0' ? 'neon' : 'coral'}>
            {/* [D-784] OWNER 31.10: «зараз повинні бути вимкнені але я повинен мати можливість сам їх вмикати коли знадобиться» */}
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_studio_pop_off ?? '1') === '0'}
                  onChange={e => save({ uniq_studio_pop_off: e.target.checked ? '0' : '1' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Учням Студії - ефектні титри «за людиною» і маска автора
              </Typography>}
            />
            <Note>
              Вимкнено з 07.10 (D-479): маска автора - найдовша стадія обробки (~12&nbsp;хв на версію).
              Увімкнули - нові обробки в Студії знову з ефектними титрами; вимкнули - класичні субтитри.
            </Note>
          </Card>
<Card title="Якість маски автора"
                chip={`крок ${Number(s.uniq_hyper_seg_every || 2)} · ${Number(s.uniq_hyper_seg_px || 512)} px`}
                chipColor="neon">
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                ['uniq_hyper_seg_every', 'Крок кадрів (1-4)', 2],
                ['uniq_hyper_seg_px', 'Роздільність, px', 512],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Note>
              Найдовша стадія обробки. Вимір на 8&nbsp;с відео:<br />
              крок&nbsp;1 — 828&nbsp;с · крок&nbsp;2 — 483&nbsp;с · крок&nbsp;3 — 307&nbsp;с.<br />
              Крок&nbsp;1 не лише найповільніший, а й найбільш «мигтячий»:
              кожен кадр рахується окремо, і шум мережі нічим не згладжується.
            </Note>
          </Card>
          </>)}
          {section === 's7' && (
<Card title="Як це працює" chipColor="neon" chip="важливо">
            <Typography sx={{ fontSize: 13, color: C.txt, lineHeight: 1.6 }}>
              Ці перемикачі — <b>дефолти для тих, хто нічого не обрав</b>.
              Сам юзер вмикає й вимикає ці функції в застосунку, на екрані
              «Посилення» → «Оформлення», і його вибір завжди головніший.
              <br /><br />
              Тут же лишаються суто технічні речі — якість маски й крок
              сегментації: вони впливають на час обробки й навантаження
              серверів, і юзеру їх бачити нема сенсу.
            </Typography>
          </Card>
          )}
  

        </Box>
        </>
      )}

      {tab === 'micro' && (
        <>
          <Card ui={cardFold} title="Навіщо це" chipColor="neon" chip="пояснення">
            <Typography sx={{ fontSize: 13, color: C.txt, lineHeight: 1.6 }}>
              Платформи порівнюють відео за «відбитком»: кадр стискається до
              сітки, і запам'ятовуються співвідношення яскравостей між
              клітинками. Такий відбиток <b>спеціально стійкий</b> до
              яскравості, контрасту й шуму — крутити їх майже марно.
              <br /><br />
              Ці два прийоми б'ють у саму будову відбитка: перший зміщує
              пікселі геометрично, другий міняє яскравість саме поклітинно.
              Обидва непомітні оку — на контрольному кадрі різниця 1.1 %.
              <br /><br />
              Юзер цього не бачить і не вмикає — лише Ви.
            </Typography>
          </Card>

          <Card ui={cardFold} title="Вгнутість кадру"
                chip={String(s.uniq_fx_lens_on || '1') === '1' ? 'увімкнено' : 'вимкнено'}
                chipColor={String(s.uniq_fx_lens_on || '1') === '1' ? 'neon' : 'coral'}>
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_fx_lens_on || '1') === '1'}
                  onChange={e => save({ uniq_fx_lens_on: e.target.checked ? '1' : '0' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Ледь помітно вигинати кадр
              </Typography>}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
              {[
                ['uniq_fx_lens_min', 'Вгнутість (мінус)', -0.012],
                ['uniq_fx_lens_max', 'Випуклість (додатне)', 0.012],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Note>
              Норма ±0.012 — підібрана за НАЙГІРШИМ матеріалом. На кадрі з прямими
              лініями (двері, вікна, меблі) 0.015 уже на межі, 0.030 помітно кривить.
            </Note>
          </Card>

          <Card ui={cardFold} title="Сітка затемнення"
                chip={String(s.uniq_fx_grid_on || '1') === '1' ? 'увімкнено' : 'вимкнено'}
                chipColor={String(s.uniq_fx_grid_on || '1') === '1' ? 'neon' : 'coral'}>
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_fx_grid_on || '1') === '1'}
                  onChange={e => save({ uniq_fx_grid_on: e.target.checked ? '1' : '0' })} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Накладати плавні плями поверх кадру
              </Typography>}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
              {[
                ['uniq_fx_grid_amp', 'Сила (0-40)', 7],
                ['uniq_fx_grid_period_min', 'Крок від, px', 140],
                ['uniq_fx_grid_period_max', 'Крок до, px', 210],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Note>
              Норма: сила 7, крок 140-210 px. На рівному світлому тлі (стіна, небо)
              сила 11 вже на межі, а 39 при кроці 47 px дає видимі клітинки.
            </Note>
          </Card>

          <Card ui={cardFold} title="Повернути норму">
            <Typography sx={{ fontSize: 13, color: C.dim, mb: 1.5, lineHeight: 1.5 }}>
              Скидає всі шість значень до перевірених на різному матеріалі:
              вгнутість ±0.012, сила плям 7, крок 140-210 px.
            </Typography>
            <Button variant="outlined" size="small"
              onClick={() => save({ uniq_fx_lens_on: '1', uniq_fx_lens_min: '-0.012', uniq_fx_lens_max: '0.012', uniq_fx_grid_on: '1', uniq_fx_grid_amp: '7', uniq_fx_grid_period_min: '140', uniq_fx_grid_period_max: '210' })}
              sx={{ borderColor: C.neon, color: C.neon }}>
              Скинути до норми
            </Button>
          </Card>
        </>
      )}

      {tab === 'dub' && (
        <>
          <Card ui={cardFold} title="Переклад озвучки"
                chip={String(s.uniq_dub_enabled ?? '1') === '1' ? 'увімкнено' : 'вимкнено'}
                chipColor={String(s.uniq_dub_enabled ?? '1') === '1' ? 'neon' : 'coral'}>
            <FormControlLabel
              control={
                <Switch checked={String(s.uniq_dub_enabled ?? '1') === '1'}
                  onChange={e => save({ uniq_dub_enabled: e.target.checked ? '1' : '0' })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: C.neon },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      bgcolor: C.neon, opacity: .45 },
                  }} />
              }
              label={<Typography sx={{ fontSize: 14, color: C.txt }}>
                Показувати блок перекладу в застосунку
              </Typography>}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
              {[
                ['uniq_dub_price_free', '🆓 Безкоштовна (голос диктора)', 0],
                ['uniq_dub_price_pro', '💎 Якісна (голос автора, Replicate)', 20],
              ].map(([k, label, def]) => (
                <Box key={k} sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                  {priceField(k, def)}
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontSize: 12, color: C.dim, mt: 1 }}>
              Токенів за КОЖНУ почату хвилину відео · 0 = безкоштовно ·
              списується при запуску, повертається якщо озвучка не вийшла
            </Typography>
          </Card>

          {/* [D-324] Разова плата за створення голосу */}
          <Card ui={cardFold} title="Створення голосу юзера"
                chip={`${Number(s.uniq_dub_voice_setup_tokens ?? 40)} ток. разово`}
                chipColor="coral">
            {priceField('uniq_dub_voice_setup_tokens', 40)}
            <Note>
              Береться ОДИН раз із юзера — коли він уперше замовляє якісну озвучку
              своїм голосом. Далі всі його відео озвучуються безкоштовно (крім ціни
              за хвилину).
            </Note>
            <Typography sx={{ fontSize: 11.5, color: C.coral, mt: .8, lineHeight: 1.5 }}>
              🔴 Створення голосу коштує нам <b>$3</b> (ціна Replicate, не MiniMax).
              Якщо поставити 0 — перше замовлення кожного нового юзера буде збитковим.
            </Typography>
          </Card>

          {/* [D-327] Приклади, які юзер відкриває з підказки «Як це працює».
              Порожньо — застосунок показує пояснення на пальцях. */}
          <Card ui={cardFold} title="Зразки для юзерів"
                chip={(s.uniq_dub_sample_free || s.uniq_dub_sample_pro) ? 'задано' : 'не задано'}
                chipColor={(s.uniq_dub_sample_free || s.uniq_dub_sample_pro) ? 'neon' : 'coral'}>
            {[
              ['uniq_dub_sample_free', '🆓 Приклад: голос диктора'],
              ['uniq_dub_sample_pro', '💎 Приклад: голос самого автора'],
            ].map(([k, label]) => (
              <Box key={k} sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .5 }}>{label}</Typography>
                <TextField
                  size="small" fullWidth placeholder="https://mediabot.uk/media/…mp4"
                  defaultValue={s[k] || ''}
                  onBlur={e => save({ [k]: String(e.target.value || '').trim() })}
                  inputProps={{ style: { color: C.txt, fontSize: 13 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,.05)' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line },
                  }}
                />
              </Box>
            ))}
            <Note>
              Пряме посилання на коротке відео (10–20 с). Юзер побачить його в
              підказці «Як працює озвучка вашим голосом» → «🎧 Показати приклади».
              Поки порожньо — там показується пояснення схемою, без відео.
            </Note>
          </Card>

          <Card ui={cardFold} title="Скільки це коштує нам">
            {[
              ['🆓 Безкоштовна озвучка', '≈ 0.001 € за хвилину — лише переклад тексту'],
              ['💎 Якісна озвучка', '≈ 0.09 € за хвилину — озвучка MiniMax'],
              ['🎙 Створення голосу', '3.00 $ разово за юзера — клон на Replicate'],
              ['🎵 Зберегти музику', '≈ 0.025 $ за відео — розділення доріжок'],
            ].map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between',
                                 gap: 2, py: .6, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                <Typography sx={{ fontSize: 13.5, color: C.txt }}>{k}</Typography>
                <Typography sx={{ fontSize: 13, color: C.dim, textAlign: 'right' }}>{v}</Typography>
              </Box>
            ))}
            <Typography sx={{ fontSize: 11.5, color: C.dim, mt: 1 }}>
              Поки на рахунку Replicate менше $5 — діє ліміт 6 запитів на хвилину,
              і довгі ролики озвучуються помітно повільніше.
            </Typography>
          </Card>
        </>
      )}

      {/* ══════════ ВКЛАДКА 3 — оплата ══════════ */}
      {tab === 'pay' && (
        <>
          {pay === null ? (
            <Typography sx={{ color: C.dim }}>Завантаження…</Typography>
          ) : (
            <>
              <Card ui={cardFold} title="Способи оплати"
                    chip={pay.payments_enabled ? 'платежі увімкнено' : 'платежі ВИМКНЕНО'}
                    chipColor={pay.payments_enabled ? 'neon' : 'coral'}>
                <Typography sx={{ fontSize: 12.5, color: C.dim, mb: 1.5, lineHeight: 1.5 }}>
                  Кожен спосіб вмикається окремо. Те, що вимкнено, юзер не бачить —
                  ні при купівлі тарифу, ні при купівлі токенів.
                </Typography>
                {payRow('💳 Картка (Stripe)', 'Автоматично, без підтвердження вручну',
                        !!pay.media_pay_show_stripe,
                        () => savePay({ media_pay_show_stripe: !pay.media_pay_show_stripe }))}
                {payRow('🏦 Банка Monobank', 'Унікальна сума + автопошук платежу',
                        monoOn, () => saveMono(!monoOn))}
                {payRow('₮ ' + (pay.pay_crypto_label || 'USDT (TRC20)'), 'Ручне підтвердження чеком',
                        !!pay.pay_show_crypto,
                        () => savePay({ pay_show_crypto: !pay.pay_show_crypto }))}
                {payRow('💳 Картка EUR (реквізити)', 'Переказ вручну + чек',
                        !!pay.pay_show_card_eur,
                        () => savePay({ pay_show_card_eur: !pay.pay_show_card_eur }))}
                {payRow('💳 Картка UAH (реквізити)', 'Переказ вручну + чек',
                        !!pay.pay_show_card_uah,
                        () => savePay({ pay_show_card_uah: !pay.pay_show_card_uah }))}
              </Card>

              <Card ui={cardFold} title="Головний вимикач">
                <Button fullWidth variant={pay.payments_enabled ? 'outlined' : 'contained'}
                  onClick={() => savePay({ payments_enabled: !pay.payments_enabled })}
                  sx={{ textTransform: 'none', fontWeight: 800, py: 1.2,
                        bgcolor: pay.payments_enabled ? 'transparent' : 'rgba(255,122,89,.9)',
                        color: pay.payments_enabled ? C.coral : '#2a0f08',
                        borderColor: C.coral }}>
                  {pay.payments_enabled ? '🔴 Вимкнути всі платежі' : '🟢 Увімкнути платежі назад'}
                </Button>
                <Note>
                  Вимкнено → екран оплати показує «тимчасово недоступно» і кнопку
                  підтримки. Ці ж перемикачі є у ⚙️ Налаштуваннях — ключі спільні,
                  міняти можна звідки завгодно.
                </Note>
              </Card>

              <Card ui={cardFold} title="Пакети токенів">
                <Typography sx={{ fontSize: 13, color: C.txt, lineHeight: 1.6 }}>
                  Скільки токенів і за скільки продавати — у розділі
                  <b> 💵 Пакети / Тарифи</b>, чип <b>🎟 Токени</b>.
                </Typography>
                <Typography sx={{ fontSize: 12, color: C.dim, mt: .8, lineHeight: 1.5 }}>
                  Нарахувати токени конкретній людині — у її картці в розділі
                  <b> 👥 Користувачі</b>.
                </Typography>
              </Card>
            </>
          )}
        </>
      )}


      {/* [D-377] Тут модалка доречна: предмет роботи — сама діаграма,
          і крива всередині оновлюється одразу. (У розділах налаштувань
          модалка навпаки шкідлива — вона перекрила б прев'ю.) */}
      {/* [D-474] набір функцій теми */}
      <Dialog open={!!thFx} onClose={() => setThFx(null)} maxWidth="md" fullWidth
              PaperProps={{ sx: { bgcolor: '#101a24', backgroundImage: 'none', border: '1px solid ' + C.line, color: C.txt } }}>
        {thFx && (<>
          <DialogTitle>🧩 Набір теми «{thFx.n}»</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 12.5, color: C.dim, mb: 1.2 }}>
              Учень у «Студії» обирає тему — і отримує саме цей набір. «Не чіпати» — як вирішено в партнера чи вибрав сам учень.
              Тема може вимкнути ефект, але не відкрити те, що сховано партнеру.
            </Typography>
            <Box sx={{ display: 'grid', gap: .8, gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
              {TH_FX.map(([k, t]) => {
                const v = thFx.fx[k];
                return (
                  <Box key={k} sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,.03)',
                                     borderTop: `3px solid ${v === 1 ? C.neon : v === 0 ? C.coral : C.line}` }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: .6 }}>{t}</Typography>
                    <Box sx={{ display: 'flex', gap: .5 }}>
                      {[[1, '✓ вкл', C.neon], [0, '✕ викл', C.coral], [undefined, '— не чіпати', C.dim]].map(([val, l, col]) => (
                        <Button key={l} size="small" onClick={() => setThFx(x => ({ ...x, fx: { ...x.fx, [k]: val } }))}
                          sx={{ flex: 1, minWidth: 0, fontSize: 11, textTransform: 'none', border: `1px solid ${col}`,
                                color: v === val ? '#04121a' : col, bgcolor: v === val ? col : 'transparent' }}>{l}</Button>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {[['🎨 Колірний настрій', TH_COLOR, 'color'], ['💬 Стиль субтитрів', TH_SUB, 'sub']].map(([t, opts, key]) => (
              <Box key={key} sx={{ mt: 1.6 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: .6 }}>{t}</Typography>
                <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap' }}>
                  {opts.map(([v, l]) => (
                    <Button key={l} size="small" onClick={() => setThFx(x => ({ ...x, [key]: v }))}
                      sx={{ textTransform: 'none', fontSize: 12, border: `1px solid ${C.line}`,
                            color: thFx[key] === v ? '#04121a' : C.neonTxt, bgcolor: thFx[key] === v ? C.neon : 'transparent' }}>{l}</Button>
                  ))}
                </Box>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setThFx(null)} sx={{ textTransform: 'none', color: C.dim }}>Скасувати</Button>
            <Button variant="contained" onClick={saveThFx} sx={{ textTransform: 'none', bgcolor: C.neon, color: '#04121a', fontWeight: 700 }}>Зберегти набір</Button>
          </DialogActions>
        </>)}
      </Dialog>
      <Dialog open={profOpen} onClose={() => setProfOpen(false)}
              maxWidth="lg" fullWidth
              PaperProps={{ sx: {
                bgcolor: C.card, backgroundImage: 'none',
                border: `1px solid ${C.line}`,
                // [D-378] тягнути за заголовок, міняти розмір за правий
                // нижній кут — `resize` браузер уміє сам, дописувати
                // нічого не треба
                transform: `translate(${dlgPos.x}px, ${dlgPos.y}px)`,
                resize: 'both', overflow: 'auto',
                minWidth: 520, minHeight: 420,
              } }}>
        <DialogTitle
          onMouseDown={(e) => {
            dlgDrag.current = { sx: e.clientX, sy: e.clientY,
                                ox: dlgPos.x, oy: dlgPos.y };
          }}
          sx={{ color: C.txt, fontWeight: 800, fontSize: 17,
                cursor: 'move', userSelect: 'none' }}>
          Форма руху камери
          <Typography component="span" sx={{ fontSize: 12.5, color: C.dim, ml: 1.5 }}>
            тягніть точки вгору-вниз
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 520px', minWidth: 0 }}>
          <canvas ref={profCanvas} width={1000} height={340}
            style={{ width: '100%', borderRadius: 8, background: '#0b0f14',
                     border: `1px solid ${C.line}`, cursor: 'ns-resize',
                     touchAction: 'none' }} />
          </Box>
          {/* [D-426] той самий ДО/ПІСЛЯ — тягнеш точку, кадр одразу стає на цей момент */}
          <Box sx={{ width: 300, flexShrink: 0 }}>
            <HyperCompare compact s={s} feats={feats} curve={pvData} seed={pvSeed} onSeed={setPvSeed}
              onDuration={(d) => { setPvDur(d); setPvTime(0); }} focus={hcFocus} />
          </Box>
          </Box>
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={
              <Switch checked={String(s.uniq_dir_manual || '0') === '1'}
                onChange={e => save({ uniq_dir_manual: e.target.checked ? '1' : '0' })} />
            }
            label={<Typography sx={{ fontSize: 13.5, color: C.txt }}>
              <b>Крива вручну</b> — рух іде саме так, як намальовано
            </Typography>}
          />
          <Typography sx={{ fontSize: 12, color: C.dim, mb: 1, lineHeight: 1.55 }}>
            Увімкнено: сходинки зміни планів притишуються до ледь помітних
            (множник 0.12), і форму веде ваша крива. Саме так можна зробити
            «перші секунди суттєво, середина на мікрорівні, кінець знову
            суттєво» — планами це недосяжно, бо вони йдуть рівномірно по
            всьому ролику.<br />
            Вимкнено: профіль лише підправляє автоматичний рух.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            {[['рівно', new Array(PROF_N).fill(0)],
              // [D-424] голова 9 · середина 8 · хвіст 7
              ['краї', [26, 24, 22, 19, 15, 11, 7, 5, 4, 3, 3, 3, 3, 3, 3, 3, 3, 4, 7, 12, 18, 22, 24, 26]],
              ['старт', [28, 26, 24, 21, 17, 13, 9, 6, 4, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3]],
              ['фініш', [3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 5, 9, 14, 19, 23, 26, 28]],
              ['пік на 2-й', [4, 9, 16, 24, 28, 22, 14, 8, 4, 3, 3, 3, 3, 3, 3, 3, 3, 4, 6, 8, 10, 12, 13, 14]],
              ['наростання', [2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 8, 10, 12, 14, 16, 18, 20, 22, 23, 24, 25, 26, 27, 27.5, 28]],
              ['віддалення на старті', [-18, -15, -13, -10, -8, -5, -3, 0, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 4, 6, 8, 10]],
              ['хвиля ±', [12, 6, -2, -8, -10, -6, 2, 8, 12, 6, -6, 8, -8, 6, -6, 4, -4, 0, 4, 8, 10, 8, 4, 0]]
             ].map(([lbl, v]) => (
              <Button key={lbl} size="small" variant="outlined"
                onClick={() => saveProf(v)}
                sx={{ textTransform: 'none', fontSize: 12,
                      borderColor: C.line, color: C.txt }}>
                {lbl}
              </Button>
            ))}
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="outlined"
              onClick={() => save({ uniq_dir_profile: '' })}
              sx={{ textTransform: 'none', fontSize: 12,
                    borderColor: C.line, color: C.coral }}>
              прибрати профіль
            </Button>
          </Box>
          <Typography sx={{ fontSize: 12.5, color: C.dim, mt: 2, lineHeight: 1.6 }}>
            🔴 Профіль задає <b>форму</b>, а не саму лінію. Крива кожної версії
            будується заново — свої плани, свій дрейф, свій вступ; профіль лише
            веде їх спільним малюнком. Якби ви малювали саму лінію, усі
            п'ятдесят копій вийшли б однаковими, і унікалізація б зникла.<br />
            Тонка лінія позаду — реальна крива версії {pvSeed + 1}: видно, як
            профіль її веде.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfOpen(false)}
            sx={{ color: C.neon, textTransform: 'none' }}>Готово</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={1800} onClose={() => setToast('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" sx={{ fontSize: 13 }}>{toast}</Alert>
      </Snackbar>
      <Snackbar open={!!err} autoHideDuration={4000} onClose={() => setErr('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" variant="filled" sx={{ fontSize: 13 }}>{err}</Alert>
      </Snackbar>
      </Box>

      {/* PREVIEW_COL [D-373] Права колонка на рівні ВСІЄЇ сторінки —
          тому починається біля заголовка, а не під смугою з версією. */}
      {['dir', 'fx', 'micro'].includes(tab) && (
      <Box sx={{ width: 410, flexShrink: 0, position: 'sticky', top: 12,
                     alignSelf: 'flex-start', maxHeight: 'calc(100vh - 24px)',
                     overflowY: 'auto', pr: .5 }}>
            {/* [D-426] ДО / ПІСЛЯ на реальному відео: кожна зміна — одразу видно */}
            <Card ui={cardFold} title="ДО / ПІСЛЯ" chip="на твоєму відео">
              <HyperCompare s={s} feats={feats} curve={pvData} seed={pvSeed} onSeed={setPvSeed}
                onDuration={(d) => { setPvDur(d); setPvTime(0); }} focus={hcFocus}
                onOpenCurve={() => setProfOpen(true)} />
            </Card>
            {tab === 'dir' && (
            <Card ui={cardFold} title="Прев'ю"
                  chip={pvBusy ? 'оновлюю…'
                        : (pvData ? `${pvData.n_shots} планів` : '…')}
                  chipColor={pvBusy ? 'coral' : 'neon'}>
              <Box sx={{
                position: 'relative', borderRadius: '10px',
                transition: 'box-shadow .2s',
                ...(pvFlash ? {
                  animation: 'pvFlash 1s ease-in-out 5',
                  '@keyframes pvFlash': {
                    '0%,100%': { boxShadow: '0 0 0 0 rgba(255,122,89,0)' },
                    '50%': { boxShadow: '0 0 0 3px rgba(255,122,89,.95)' },
                  },
                } : {}),
              }}>
                <canvas ref={pvCanvas} width={300} height={330}
                  style={{ borderRadius: 10, background: '#0b0f14', width: '100%',
                           display: 'block',
                           opacity: pvBusy ? .45 : 1,
                           transition: 'opacity .15s',
                           border: `1px solid ${C.line}` }} />
                {pvBusy && (
                  <Typography sx={{
                    position: 'absolute', inset: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, color: C.coral, fontWeight: 700,
                    pointerEvents: 'none',
                  }}>
                    рахую нову криву…
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <canvas ref={pvMini} width={72} height={96}
                  style={{ borderRadius: 4, background: '#0b0f14',
                           border: `1px solid ${C.line}`, flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: 12.5, color: C.txt }}>
                    видно <b style={{ color: C.neon }}>{pvDiff.seen}</b> кадру
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.dim, lineHeight: 1.4, mt: .3 }}>
                    <span style={{ color: '#4da6ff' }}>синє</span> — увесь оригінал,
                    <span style={{ color: C.neon }}> зелене</span> — що в кадрі.
                    Головне вікно вгорі — вже результат.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ px: .5, mt: .5 }}>
                <Slider size="small" min={0} max={Math.max(0.1, pvDur)} step={0.02}
                  value={pvTime}
                  onChange={(e, v) => { setPvPlay(false); setPvTime(v); }}
                  sx={{ color: C.neon,
                        '& .MuiSlider-thumb': { width: 16, height: 16 } }} />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <Button size="small" variant="contained"
                  onClick={() => setPvPlay(p => !p)}
                  sx={{ minWidth: 92, bgcolor: C.neon, color: '#04121a',
                        '&:hover': { bgcolor: C.neon } }}>
                  {pvPlay ? '⏸ Пауза' : '▶ Програти'}
                </Button>
                <Button size="small" variant="outlined"
                  onClick={() => { setPvPlay(false); setPvTime(0); }}
                  sx={{ minWidth: 40, borderColor: C.line, color: C.dim }}>⏮</Button>
                <Typography sx={{ fontSize: 12, color: C.dim, fontFamily: 'monospace' }}>
                  {pvTime.toFixed(2)} с
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .7 }}>
                  <Box sx={{ width: 14, height: 3, bgcolor: '#4da6ff', borderRadius: 1 }} />
                  <Typography sx={{ fontSize: 11.5, color: C.dim }}>оригінал</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .7 }}>
                  <Box sx={{ width: 14, height: 3, bgcolor: C.neon, borderRadius: 1 }} />
                  <Typography sx={{ fontSize: 11.5, color: C.dim }}>після обробки</Typography>
                </Box>
              </Box>

              <Box sx={{ bgcolor: 'rgba(0,229,160,.07)', borderRadius: 1.5, p: 1.2, mb: 1.5 }}>
                <Typography sx={{ fontSize: 12.5, color: C.txt }}>
                  Різниця з оригіналом: <b style={{ color: C.neon }}>{pvDiff.now}</b>
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: C.dim, mt: .3 }}>
                  на першому кадрі: <b style={{ color: pvDiff.firstBad ? C.coral : C.neon }}>
                    {pvDiff.first}</b> · найбільша за ролик: {pvDiff.max}
                </Typography>
              </Box>



              <canvas ref={pvGraph} width={300} height={78}
                onClick={() => setProfOpen(true)}
                title="Натисніть, щоб відкрити велику діаграму"
                style={{ width: '100%', borderRadius: 8, background: '#0b0f14',
                         border: `1px solid ${C.line}`, cursor: 'zoom-in' }} />
              <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mt: .6 }}>
                {[['rgba(77,166,255,.5)', 'вступ'],
                  ['rgba(0,229,160,.35)', 'зум у сцені'],
                  ['rgba(234,246,255,.35)', 'стик планів']].map(([c, t]) => (
                  <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                    <Box sx={{ width: 11, height: 11, bgcolor: c, borderRadius: .5 }} />
                    <Typography sx={{ fontSize: 10.5, color: C.dim }}>{t}</Typography>
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontSize: 10.5, color: C.dim, mt: .4 }}>
                Синя пряма — рівень оригіналу.
              </Typography>
            </Card>
            )}


          </Box>
      )}
    </Box>
  );
}

const swSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: C.neon },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    bgcolor: C.neon, opacity: .45 },
};

const sliderSx = {
  color: '#00e5a0',
  '& .MuiSlider-markLabel': { color: '#a8bece', fontSize: 11 },
  '& .MuiSlider-rail': { opacity: .25 },
};
